/**
 * CYLINDERS (item 3).
 *
 * Search, and the passport behind each one. ADR-008's rule is what this screen
 * is for: a cylinder's state is SEVERAL facts, not one word. Where it is, who
 * holds it, what is in it and what it is doing are shown as separate columns,
 * because "available" is a conclusion drawn from all of them and a screen that
 * prints one word invites the wrong conclusion.
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { api, can, type Session, type Views } from '../api.js';
import { useAsk, useRun, go, useRouteQuery } from '../hooks.js';
import { Banner, Button, Card, Kpi, Loading, Money, Pill, Register, Serial, Text, TextArea, Select, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { FleetTabs } from '../fleet.js';
import { CustomerPickerById } from '../pickers.js';
import { count, date, days, rupeesToPaise, today, words, when } from '../format.js';
import { actionWords, whereItIs } from '../standing.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('cylinders')!.messages;


/**
 * THE STOCK FACE (audit J6, docs/CTS2-USER-JOURNEYS.md): "how many filled
 * oxygen cylinders do we have?" must be an answer, not a filtering exercise.
 * Four live counts over the same register, each a click that applies the
 * filter it counts. The gas filter scopes them; the totals are the view's own.
 */
function StockStrip({ gas, state, onPick }: { gas: string; state: string; onPick: (s: string) => void }) {
  const one = (s: string) => api.view('cylinders.search', { state: s, ...(gas ? { gasService: gas } : {}), pageSize: 1 });
  const filled = useAsk(() => one('filled_in_plant'), [gas], MESSAGES);
  const empty = useAsk(() => one('empty_in_plant'), [gas], MESSAGES);
  const out = useAsk(() => one('at_customer'), [gas], MESSAGES);
  const transit = useAsk(() => one('in_transit'), [gas], MESSAGES);
  const tile = (label: string, s: string, asked: { data: { page: { total: number } } | null }, tone: 'good' | 'empty' | 'primary' | 'muted', note: string) => (
    <button type="button" className="kpi-pick" aria-pressed={state === s} onClick={() => onPick(s)}>
      <Kpi label={label} value={asked.data ? count(asked.data.page.total) : '…'} tone={tone} note={note} />
    </button>
  );
  return (
    <div className="kpis" style={{ marginBottom: 12 }}>
      {tile('Filled, in plant', 'filled_in_plant', filled, 'good', gas ? `ready to send · ${gas}` : 'ready to send · all gases')}
      {tile('Empty, in plant', 'empty_in_plant', empty, 'empty', 'waiting on filling')}
      {tile('Out with customers', 'at_customer', out, 'primary', 'earning holding')}
      {tile('In transit', 'in_transit', transit, 'muted', 'on a vehicle, either way')}
    </div>
  );
}

export function Cylinders({ id, session }: { id: string | null; session: Session }) {
  if (id === 'new') return <RegisterCylinder session={session} />;
  if (id) return <Passport cylinderId={Number(id)} session={session} />;
  return <CylinderSearch session={session} />;
}

/**
 * THE CENSUS (db/queries/01_where_is_everything.sql, exposed 22 Sep): every
 * active cylinder in exactly one row, reconciled against the registry's own
 * count. The strip above answers "how many filled oxygen cylinders" one gas
 * at a time; this answers "where is EVERYTHING", all gases, in one look —
 * and says out loud if the rows below it do not add up to the total, rather
 * than quietly showing a wrong sum (D231).
 */
function Census() {
  const asked = useAsk(() => api.view('cylinders.census', {}), [], MESSAGES);
  if (asked.loading && !asked.data) return <Card title="Where every cylinder is"><Loading what="the census" /></Card>;
  if (asked.error) return <Card title="Where every cylinder is"><Banner kind="warn" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const c = asked.data!;
  const columns: readonly Column<{ state: string; cylinders: number; oldestSince: string | null; whatItMeans: string }>[] = [
    { key: 'state', head: 'Where / what', cell: (r) => <Pill {...(r.state === 'unaccounted' || r.state === 'no position recorded' ? { tone: 'danger' as const } : {})}>{r.state}</Pill> },
    { key: 'cylinders', head: 'Cylinders', right: true, cell: (r) => count(r.cylinders) },
    { key: 'since', head: 'Oldest since', cell: (r) => r.oldestSince ? date(r.oldestSince) : <span className="muted">—</span> },
    { key: 'means', head: 'What it means', cell: (r) => <span className="muted">{r.whatItMeans}</span> },
  ];
  return (
    <Card title="Where every cylinder is" sub="every active cylinder appears in exactly one row below">
      {!c.reconciled ? (
        <Banner kind="error" title="These rows do not add up to the registry's own count."
                detail={`The registry counts ${count(c.totalActive)} active cylinders; the rows below add to a different figure. Tell a manager.`} />
      ) : null}
      {/*
        * A06 — "ACTIVE" WAS HIDING A DISAGREEMENT. A cylinder that has been physically
        * destroyed keeps `lifecycle = 'active'` while custody cannot honestly end its life
        * — it is out with a customer, in transit, or on a trip whose receipt challan has
        * not been found. It is counted in the fleet and it does not exist, and one number
        * for "active" made that invisible in exactly the figure a person checks.
        */}
      {c.fleet.awaitingFinalisation > 0 ? (
        <Banner kind="warn" title={`${count(c.fleet.awaitingFinalisation)} of these no longer exist.`}
                detail={`${count(c.fleet.physicallyActive)} cylinders are really there. ${count(c.fleet.awaitingFinalisation)} have been physically destroyed and are still counted, because the registry entry could not be closed — see "Destroyed, and still on the books" below for what is in the way. ${count(c.fleet.fullyScrapped)} have had their life ended and are outside this count entirely.`} />
      ) : null}
      <Register columns={columns} rows={c.rows} empty="There are no active cylinders in the registry."
                totals={<span>
                  Total active in the registry: <b>{count(c.totalActive)}</b>
                  {c.fleet.awaitingFinalisation > 0
                    ? <> — of which <b>{count(c.fleet.physicallyActive)}</b> physically exist and <b>{count(c.fleet.awaitingFinalisation)}</b> await finalisation</>
                    : null}
                </span>} asOf={date(c.asOf)} />
    </Card>
  );
}

function CylinderSearch({ session }: { session: Session }) {
  const [q, setQ] = useState('');
  const [gas, setGas] = useState('');
  const [state, setState] = useState('');
  // FROM THE ADDRESS, because home links here. The red "Test overdue" figure on the front
  // page is a safety figure (D267 — an overdue vessel must not be filled), and a figure
  // that lands on an unfiltered register of eight thousand cylinders answers nothing. The
  // question it raises is "which ones", so the link carries `?test=overdue` and arrives
  // with the answer on screen. useRouteQuery SUBSCRIBES, so a second click from home while
  // already on this screen re-filters rather than doing nothing.
  const fromAddress = useRouteQuery('test') ?? '';
  const [test, setTest] = useState(fromAddress);
  useEffect(() => { setTest(fromAddress); }, [fromAddress]);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [q, gas, state, test]);
  const asked = useAsk(() => api.view('cylinders.search', {
    ...(q.trim() ? { q: q.trim() } : {}), ...(gas ? { gasService: gas } : {}), ...(state ? { state } : {}),
    ...(test ? { test: test as 'overdue' } : {}), page, pageSize: 50,
  }), [q, gas, state, test, page], MESSAGES);

  const columns: readonly Column<{
    cylinderId: number; internalId: string; serialNo: string; gasService: string; ownerKind: string;
    state: string | null; holder: string | null; holderName: string | null; since: string | null; lifecycle: string; replacementCostPaise: number;
    openTrip: { customerId: number; daysOut: number } | null;
    awaitingFinalisation: boolean;
  }>[] = [
    { key: 'id', head: 'Cylinder', cell: (r) => <Serial>{r.internalId}</Serial> },
    { key: 'serial', head: 'Serial', cell: (r) => r.serialNo },
    { key: 'gas', head: 'Gas', cell: (r) => words(r.gasService) },
    { key: 'owner', head: 'Owner', cell: (r) => <Pill tone={r.ownerKind === 'company' ? 'primary' : 'testing'}>{r.ownerKind === 'company' ? 'ours' : "customer's"}</Pill> },
    // audit Y1/Y2 — one phrase, not a raw state beside a raw `customer:5`
    { key: 'where', head: 'Where it is', cell: (r) => {
        const w = whereItIs(r.state, r.holder, r.holderName, r.openTrip?.daysOut ?? null);
        return <>
          <Pill tone={w.tone}>{w.word}</Pill>
          {/* A06 — this one has been DESTROYED and is still in the register, because
            * custody could not end its life. The census has said so since A06 shipped and
            * this list did not, so a vessel that no longer exists sat among the live ones
            * looking ordinary. */}
          {r.awaitingFinalisation ? <> <Pill tone="danger">destroyed — not finished</Pill></> : null}
          {w.note ? <div className="muted">{w.note}</div> : null}
        </>;
      } },
    { key: 'since', head: 'Since', cell: (r) => date(r.since) },
  ];

  return (
    <Page title="Cylinders" sub="Where each cylinder is, who holds it and what is in it."
          actions={can(session, 'cylinder.register') ? <Button kind="primary" onClick={() => go('#/cylinders/new')}>Register a cylinder</Button> : null}>
      <FleetTabs active="cylinders" session={session} />
      <StockStrip gas={gas} state={state} onPick={(s) => setState(s === state ? '' : s)} />
      {/* 0108 (DEF-168) — what the yard must do something about, above the register
        * rather than beside it: a cylinder overdue for its periodic test may not be
        * filled, and until 0108 nothing in this system could tell anybody which ones. */}
      {can(session, 'view.maintenance') ? <DueForTest /> : null}
      <PendingDestruction session={session} />
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="q">Search</label>
          <input id="q" type="text" value={q} placeholder="Internal ID or serial" onChange={(e: { target: { value: string } }) => setQ(e.target.value)} />
        </div>
        <Select name="gas" label="Gas" value={gas} onChange={setGas} placeholder="Any gas"
                options={[{ value: 'oxygen', label: 'Oxygen' }, { value: 'acetylene', label: 'Acetylene' }, { value: 'nitrogen', label: 'Nitrogen' }, { value: 'argon', label: 'Argon' }, { value: 'co2', label: 'CO₂' }]} />
        <Select name="state" label="Where / what" value={state} onChange={setState} placeholder="Any state"
                options={[{ value: 'filled_in_plant', label: 'Filled, in plant' }, { value: 'empty_in_plant', label: 'Empty, in plant' },
                          { value: 'at_customer', label: 'With a customer' }, { value: 'in_transit', label: 'In transit' }]} />
        {/* Book 3's DERIVED status (0108-0111): never stored, so a cylinder goes overdue
          * because time passed. The labels say what each one MEANS to the person filling —
          * "overdue" and "failed" both stop a fill and for different reasons, and neither
          * should reach a screen as a database token. */}
        <Select name="test" label="Test status" value={test} onChange={setTest} placeholder="Any test status"
                options={[{ value: 'overdue', label: 'Overdue — must not be filled' },
                          { value: 'failed', label: 'Failed its last test' },
                          { value: 'due_soon', label: 'Due within a month' },
                          { value: 'current', label: 'In date' },
                          { value: 'condemned', label: 'Condemned' },
                          { value: 'unknown', label: 'No test recorded' }]} />
      </div>
      {test ? (
        <p className="muted">
          Showing only cylinders whose test status is <strong>{test === 'due_soon' ? 'due within a month' : test}</strong>.
          {' '}Clear the filter above to see the whole register.
        </p>
      ) : null}
      {/* NAMED so a test can ask about THIS table. `heads()` read every `table.register`
        * on the page, so a panel above (the census, the destruction queue) satisfied its
        * wait before the register had rendered and it returned the panel's headings —
        * DEF-234's shape again, and it went red in CI. */}
      <Card className="cylinder-register">
        {asked.loading && !asked.data ? <Loading what="cylinders" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No cylinder matches that."
                        onOpen={(r) => go(`#/cylinders/${r.cylinderId}`)}
 />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
      <Census />
    </Page>
  );
}

type PassportAction = 'correct' | 'owner' | 'gas' | 'end';

/** Which book the person is looking at. One at a time, because a phone has one screen. */
type Book = 'facts' | 'trips' | 'work' | 'movements' | 'identity' | 'paper' | 'label' | 'money';

/**
 * The book a URL's `?book=` asks for; `facts` for anything else, including nothing.
 *
 * Exported for the same reason `chosenTab` is on the customer page: it is the whole of the
 * fallback rule, and a rule that decides what a bookmarked address opens is worth a test
 * that does not need a browser.
 */
export function asBook(asked: string | null): Book {
  return BOOK_TABS.some((t) => t.key === asked) ? asked as Book : 'facts';
}

const BOOK_TABS: { key: Book; label: string }[] = [
  { key: 'facts', label: 'Where it stands' },
  { key: 'trips', label: 'Its trips' },
  { key: 'work', label: 'Work done on it' },
  { key: 'movements', label: 'Every movement' },
  { key: 'identity', label: 'What was changed' },
  { key: 'paper', label: 'Paperwork' },
  { key: 'label', label: 'Its label' },
  { key: 'money', label: 'Money' },
];

/**
 * THE PASSPORT — ONE PAGE FOR ONE PHYSICAL CYLINDER.
 *
 * A storeman holding the vessel, on a phone, in a yard, must be able to answer the whole
 * story of it without opening another register. That is the whole specification, and
 * everything below follows from it:
 *
 * — THE IDENTITY STAYS ON SCREEN. It is a sticky header, because a person who has scrolled
 *   into the movement book and is deciding something must still be able to see WHICH
 *   cylinder he is deciding it about. Reading the serial at the top, scrolling, and then
 *   acting is how the wrong vessel gets filled.
 *
 * — THE SEVEN FACTS ARE SEVEN TILES, never one word (ADR-008, D264–D267). Each carries the
 *   date its fact was established and says `unknown` out loud when the source does not
 *   know. "Available" is a conclusion drawn from all seven and this page refuses to draw
 *   it for the reader.
 *
 * — WHAT IS BLOCKED IS AT THE TOP, WITH WHAT IS STILL ALLOWED BESIDE IT. A page that says
 *   "quarantined" and stops has told a storeman the cylinder is untouchable, when in fact
 *   he may still move it to the testing bay. The server computes both halves from the one
 *   safety matrix, so this screen cannot disagree with the commands that refuse him.
 *
 * — HISTORY LOOKS LIKE HISTORY. A line written down after the day it happened is marked
 *   `late`, a voided movement is struck through, and a superseded record says so. Current
 *   truth is in the tiles at the top; everything in a book is the past.
 *
 * — EACH BOOK PAGES ON THE SERVER, independently, and every page change refetches from the
 *   server rather than slicing something held here. A cylinder bought in 2009 has a
 *   thousand movements.
 *
 * — AN ERROR NEVER LOOKS LIKE AN EMPTY BOOK. "No trips recorded" and "we could not read the
 *   trips" are different sentences and the second one is a banner, because a storeman who
 *   reads the first when the second is true concludes the cylinder has never been out.
 */
function Passport({ cylinderId, session }: { cylinderId: number; session: Session }) {
  /**
   * WHICH BOOK IS OPEN LIVES IN THE URL, like the customer page's tabs.
   *
   * Not for tidiness. The app has no router library and routes on the hash precisely
   * because "a yard tablet reloads and a bookmarked challan must open the challan"
   * (ui/src/app.tsx). A book held only in component state loses that: a storeman who
   * reloads on a bad signal lands back on the first book, and he cannot send anybody a
   * link to the trip he is looking at.
   *
   * It is also what the browser suite found. Navigating to `#/cylinders/7` while already
   * on `#/cylinders/7` changes no hash, so React never remounts and the book stayed
   * wherever the last step left it — the step looked for the fact tiles and was shown the
   * money book. That was a test artifact of a REAL defect: the address did not describe
   * the page it opened.
   */
  // ...and it is read with `useRouteQuery`, which SUBSCRIBES to the address (Slice 4B).
  // `routeQuery` reads once per render, and a hash change that alters only the query does
  // not re-render this component — `useRoute` watches the path's parts, which are the same.
  // So a link into a particular book from somewhere else left the page on whatever book it
  // was already showing. Found by the browser suite walking from the passport to its own
  // movement book; the same shape the workbench had.
  const book = asBook(useRouteQuery('book'));
  const setBook = (b: Book) => go(`#/cylinders/${cylinderId}?book=${b}`);
  const [pages, setPages] = useState<{ trips: number; movements: number; identity: number; work: number; documents: number; damage: number }>(
    { trips: 1, movements: 1, identity: 1, work: 1, documents: 1, damage: 1 });
  const [open, setOpen] = useState<PassportAction | null>(null);

  // ONE REQUEST FOR THE WHOLE PAGE, re-made when any book turns a page. The alternative —
  // one request per book — multiplies the round trips a yard phone waits on, and the
  // server assembles all of it in a dozen bounded queries whatever the page asks for.
  const asked = useAsk(() => api.view('cylinders.passport', {
    cylinderId,
    tripsPage: pages.trips, tripsPageSize: 20,
    movementsPage: pages.movements, movementsPageSize: 25,
    identityPage: pages.identity, identityPageSize: 20,
    workPage: pages.work, workPageSize: 25,
    documentsPage: pages.documents, documentsPageSize: 20,
    damagePage: pages.damage, damagePageSize: 20,
  }), [cylinderId, pages.trips, pages.movements, pages.identity, pages.work, pages.documents, pages.damage], MESSAGES);

  if (asked.loading) return <Page title="Cylinder"><Loading what="this cylinder's passport" /></Page>;
  if (asked.error) {
    return (
      <Page title="Cylinder">
        <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
      </Page>
    );
  }
  const p = asked.data!;
  const i = p.identity;
  const done = () => { setOpen(null); asked.reload(); };
  const toggle = (which: PassportAction) => setOpen((v) => (v === which ? null : which));
  const turn = (which: keyof typeof pages) => (n: number) => setPages((v) => ({ ...v, [which]: n }));

  return (
    <Page title={`Cylinder ${i.internalId}`}
          actions={p.ended ? <Pill tone="danger">{`${i.lifecycle} — its record is kept, nothing more happens to it`}</Pill> : <>
            {can(session, 'cylinder.correct') ? <Button onClick={() => toggle('correct')}>{open === 'correct' ? 'Leave it' : 'Correct the details'}</Button> : null}
            {can(session, 'cylinder.transfer_owner') ? <Button onClick={() => toggle('owner')}>{open === 'owner' ? 'Leave it' : 'Transfer ownership'}</Button> : null}
            {can(session, 'cylinder.convert_gas') ? <Button onClick={() => toggle('gas')}>{open === 'gas' ? 'Leave it' : 'Convert the gas'}</Button> : null}
            {can(session, 'cylinder.end_life') ? <Button kind="danger" onClick={() => toggle('end')}>{open === 'end' ? 'Keep it in service' : 'Lost or scrapped'}</Button> : null}
          </>}>

      <IdentityHeader identity={i} asOf={p.asOf} holder={holderOf(p)} />

      {/* 12 Oct — CTS1's four tiles: the state, how long it has been out, whether
        * its test stands, and how much it has moved. The seven facts below are the
        * honest model; these are the four a person reads first. */}
      <PassportTiles passport={p} />

      {/* WHAT IS STOPPING IT, FIRST. Above the books, above the forms, above everything:
        * it is the only thing on this page that changes what a person is allowed to do
        * next. */}
      <Blockers passport={p} />

      {open === 'correct' ? <CorrectCylinder cylinder={{ cylinderId: i.cylinderId, serialNo: i.serialNo, waterCapacityL: i.waterCapacityL, replacementCostPaise: i.replacementCostPaise, manufacturer: i.manufacturer, yearOfManufacture: i.yearOfManufacture === null ? null : Number(i.yearOfManufacture) }} onDone={done} /> : null}
      {open === 'owner' ? <TransferOwner cylinderId={cylinderId} ownerKind={i.ownerKind} onDone={done} /> : null}
      {open === 'gas' ? <ConvertGas cylinderId={cylinderId} from={i.gasService} onDone={done} /> : null}
      {open === 'end' ? <EndLife cylinderId={cylinderId} internalId={i.internalId} onDone={done} /> : null}

      <nav className="book-tabs" aria-label="This cylinder's books">
        {BOOK_TABS.map((t) => (
          <button key={t.key} type="button" className="book-tab" aria-pressed={book === t.key}
                  onClick={() => setBook(t.key)}>{t.label}</button>
        ))}
      </nav>

      {book === 'facts' ? <>
        {/* 12 Oct — CTS1 puts the movement timeline, what it is, its papers and its
          * hold periods ON THE PAGE. CTS2 had every one of them behind a tab, so the
          * first thing a person wants to know — where has this vessel been — cost a
          * click and a guess at which book held it. The books stay for the full
          * registers; these are the latest of each, where CTS1 has them. */}
        <RecentMovements passport={p} onAll={() => setBook('movements')} />
        <WhatItIs identity={i} />
        {/* BOOK 3 (0108, DEF-168) — whether this vessel is fit to fill is the first thing
          * anybody opening this page needs, and it has its own screen-within-a-screen
          * because recording a test is a different act from reading one. */}
        <MaintenanceBook cylinderId={cylinderId} session={session} onDone={done} />
        <HoldPeriods passport={p} />
        <RecentPapers passport={p} onAll={() => setBook('paper')} />
        {/* The seven, last: they are the model the rest of this page is read FROM,
          * and a person who wants them is looking for them. */}
        <SevenFacts facts={p.facts} />
      </> : null}

      {book === 'trips' ? <TripsBook passport={p} onPage={turn('trips')} /> : null}
      {book === 'work' ? <WorkBook passport={p} onPage={turn('work')} /> : null}
      {book === 'movements' ? <MovementBook passport={p} onPage={turn('movements')} /> : null}
      {book === 'identity' ? <>
        <IdentityBook passport={p} onPage={turn('identity')} />
        <ChangeHistory cylinderId={cylinderId} />
      </> : null}
      {book === 'paper' ? <>
        <PaperBook passport={p} onPage={turn('documents')} />
        <DamageBook passport={p} onPage={turn('damage')} />
      </> : null}
      {book === 'label' ? <LabelBook passport={p} cylinderId={cylinderId} session={session} onDone={done} /> : null}
      {book === 'money' ? <MoneyBook passport={p} /> : null}
    </Page>
  );
}

/** The passport, exactly as the server publishes it. Named once so no component redeclares it. */
type PassportData = Views['cylinders.passport']['result'];

/**
 * THE STICKY IDENTITY. What is stamped on the vessel, and the warning that the stamping is
 * not unique.
 *
 * `sameSerialElsewhere` is the one fact this page volunteers about ANOTHER cylinder, and
 * it earns its place: a serial number is not unique in this fleet (OD-033), so a person
 * who searched "4471" and landed here may be looking at the wrong vessel. Telling him
 * costs one line; not telling him costs a wrongly filled cylinder.
 */
/**
 * THE VESSEL, FIRST (12 October, the owner: "cylinder passport needs to be redone
 * with printing sticker option inbuilt").
 *
 * CTS1 opened this page with the thing in your hand: the number large enough to
 * read across a yard, what it carries, whose it is, where it is standing — and
 * the sticker itself, printable from the page. What stood here instead was a
 * row of small grey labels, with the sticker four tabs away.
 *
 * The sticker shown is the one RECORDED as printed where there is one, because
 * that is what is glued on out there even if the hostname has since moved. Where
 * none was ever recorded the page says so rather than passing a derived label
 * off as the vessel's own.
 */
function IdentityHeader({ identity, asOf, holder }: { identity: PassportData['identity']; asOf: string; holder: string }) {
  const [printing, setPrinting] = useState(false);
  const owner = identity.ownerKind === 'company' ? 'ours' : (identity.ownerName ?? "the customer's");
  const standing = identity.yardBranch ?? (identity.yardUnknown ? 'a yard of ours, not written down' : 'not in one of our yards');
  return (
    <>
      {/* THE SLIM STICKY BAR. A person who has scrolled into the movement book and is
        * about to act must still see WHICH cylinder he is acting on — reading the serial
        * at the top, scrolling, and then acting is how the wrong vessel gets filled. The
        * hero below is the page's opening; this is the one that follows you down it. */}
      <div className="passport-head">
        <span><span className="k">CTS number</span> <b className="passport-head-id">{identity.internalId}</b></span>
        <span><span className="k">Stamped on the vessel</span> <Serial>{identity.serialNo}</Serial></span>
        <span><span className="k">Owned by</span> {identity.homeBranchCode ?? identity.homeBranch ?? '—'}</span>
        <span className="passport-head-where"><span className="k">Standing in</span> {standing}</span>
      </div>
    <div className="passport-hero">
      <div className="passport-hero-main">
        <div className="k">Cylinder passport</div>
        <div className="passport-id">{identity.internalId}</div>
        <div className="passport-chips">
          <Pill tone="primary">{words(identity.gasService)}</Pill>
          {identity.waterCapacityL ? <Pill>{`${Number(identity.waterCapacityL)} L`}</Pill> : null}
          {identity.valveSpec ? <Pill>{identity.valveSpec}</Pill> : null}
          <Pill tone={identity.ownerKind === 'company' ? 'good' : 'testing'}>{identity.ownerKind === 'company' ? 'ours' : 'the customer’s'}</Pill>
          {identity.lifecycle !== 'active' ? <Pill tone="danger">{words(identity.lifecycle)}</Pill> : null}
        </div>
        <div className="passport-currently">
          <div className="k">Currently with</div>
          <div className="passport-currently-who">{holder}</div>
        </div>
        <div className="passport-hero-facts">
          <div><div className="k">Stamped on the vessel</div><div className="v"><Serial>{identity.serialNo}</Serial></div></div>
          <div><div className="k">Whose it is</div><div className="v">{identity.ownerCustomerId
            ? <a href={`#/customers/${identity.ownerCustomerId}`}>{owner}</a> : owner}</div></div>
          {/* TWO BRANCH FACTS, SIDE BY SIDE AND NEVER ONE INSTEAD OF THE OTHER (Slice 4B):
            * which branch owns the asset, and which yard it is actually standing in. */}
          <div><div className="k">Owned by</div><div className="v">{identity.homeBranchCode ?? identity.homeBranch ?? '—'}</div></div>
          <div><div className="k">Standing in</div><div className="v">{standing}</div></div>
        </div>
      </div>
      <div className="passport-hero-label">
        {identity.label ? (
          <>
            <Sticker identity={identity} modules={identity.label.modules} mark={identity.label.markSvg} />
            <div className="passport-label-foot">
              <Button small onClick={() => setPrinting(true)}>Print the sticker</Button>
              <span className="muted">{identity.label.printed ? 'as printed on the vessel' : 'never printed — this is what a new one would say'}</span>
            </div>
          </>
        ) : (
          <p className="muted">
            No sticker can be shown: this company has no hostname configured, so a scanned
            label would have nowhere to go. Set it under Settings → Company first.
          </p>
        )}
      </div>
      {identity.sameSerialElsewhere.length > 0 ? (
        <div className="passport-hero-warn">
          <Banner kind="warn" title={`Serial ${identity.serialNo} is stamped on ${count(identity.sameSerialElsewhere.length)} other ${identity.sameSerialElsewhere.length === 1 ? 'cylinder' : 'cylinders'} — make sure this is the one in your hand.`}
                  detail={identity.sameSerialElsewhere.map((t) => `${t.internalId} (${t.ownerKind === 'company' ? 'ours' : t.ownerName ?? 'a customer'})`).join(', ')} />
        </div>
      ) : null}
      <div className="muted passport-asof">Read on {date(asOf)}.</div>
      {printing && identity.label ? <StickerPrint identity={identity} modules={identity.label.modules} mark={identity.label.markSvg} onClose={() => setPrinting(false)} /> : null}
    </div>
    </>
  );
}

/** Who is holding it right now, in the words CTS1 uses on its hero. */
function holderOf(p: PassportData): string {
  const custodian = p.facts.find((f) => f.fact === 'custodian');
  const position = p.facts.find((f) => f.fact === 'position');
  if (custodian && custodian.known && custodian.value !== 'company') return words(custodian.value);
  const open = p.trips.rows.find((t) => t.status === 'open');
  if (open?.customerName) return open.customerName;
  if (position && position.known) return p.identity.yardBranch ?? words(position.value);
  return 'not recorded';
}

/**
 * WHERE IT HAS BEEN, on the page. The latest few of the movement book, in the
 * same timeline the book draws, with a way through to all of them.
 */
function RecentMovements({ passport, onAll }: { passport: PassportData; onAll: () => void }) {
  const rows = passport.movements.rows.slice(0, 6);
  return (
    <Card title="Where it has been" sub={`${count(passport.movements.page.total)} movement(s) — newest first`}
          actions={passport.movements.page.total > rows.length
            ? <Button small onClick={onAll}>All of them</Button> : undefined}>
      {rows.length === 0 ? <p className="muted">No movement has been recorded yet.</p> : (
        <ul className="timeline">
          {rows.map((m) => (
            <li key={m.movementId} className={m.voided ? 'voided' : undefined}>
              <strong>{words(m.type)}</strong>
              {m.from || m.to ? <> — {words(m.from)} &rarr; {words(m.to)}</> : null}
              {m.voided ? <> <Pill tone="danger">voided</Pill></> : null}
              {m.recordedLate ? <> <Pill tone="testing">written down later</Pill></> : null}
              <div className="when">
                {date(m.eventDate)}
                {m.documentRef ? ` · ${m.documentRef}` : ''}
                {m.recordedByName ? ` · by ${m.recordedByName}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Its hold periods, which CTS1 shows last and which decide what may be done to it. */
function HoldPeriods({ passport }: { passport: PassportData }) {
  const holds = passport.holds;
  const open = holds.filter((h) => h.open).length;
  return (
    <Card title="Hold periods" sub={holds.length === 0 ? 'none, ever' : `${count(holds.length)} in all · ${count(open)} still open`}>
      {holds.length === 0 ? <p className="muted">Nothing has ever been held against this cylinder.</p> : (
        <Register columns={[
          { key: 'kind', head: 'What', cell: (h: PassportData['holds'][number]) => <>{words(h.kind)} {h.open ? <Pill tone="danger">open</Pill> : <Pill tone="good">lifted</Pill>}</> },
          { key: 'why', head: 'Why', cell: (h: PassportData['holds'][number]) => h.reason },
          { key: 'from', head: 'Opened', cell: (h: PassportData['holds'][number]) => when(h.openedAt) },
          { key: 'to', head: 'Lifted', cell: (h: PassportData['holds'][number]) => h.releasedAt ? <>{when(h.releasedAt)}{h.releaseReason ? <div className="muted">{h.releaseReason}</div> : null}</> : <span className="muted">still on</span> },
        ]} rows={holds} empty="" />
      )}
    </Card>
  );
}

/** The papers this vessel appears on, latest first — CTS1's Documents card. */
function RecentPapers({ passport, onAll }: { passport: PassportData; onAll: () => void }) {
  const rows = passport.documents.rows.slice(0, 5);
  return (
    <Card title="Its papers" sub={`${count(passport.documents.page.total)} document(s) name this cylinder`}
          actions={passport.documents.page.total > rows.length ? <Button small onClick={onAll}>All of them</Button> : undefined}>
      {rows.length === 0 ? <p className="muted">No paper names this cylinder yet.</p> : (
        <Register columns={[
          { key: 'kind', head: 'Paper', cell: (d: PassportData['documents']['rows'][number]) => <>{words(d.kind)}{d.number ? <div className="muted">{d.number}</div> : null}</> },
          { key: 'why', head: 'Why it is here', cell: (d: PassportData['documents']['rows'][number]) => d.because },
          { key: 'when', head: 'Issued', cell: (d: PassportData['documents']['rows'][number]) => when(d.issuedAt) },
          { key: 'open', head: '', right: true, cell: (d: PassportData['documents']['rows'][number]) => d.link
            ? <a className="btn ghost small" href={d.link} target="_blank" rel="noreferrer">Open</a> : null },
        ]} rows={rows} empty="" />
      )}
    </Card>
  );
}

/** The four a person reads first — each one a summary of a book below it. */
function PassportTiles({ passport }: { passport: PassportData }) {
  const fact = (k: string) => passport.facts.find((f) => f.fact === k);
  const position = fact('position');
  const test = fact('test_status');
  const rent = fact('rent_state');
  const open = passport.trips.rows.find((t) => t.status === 'open');
  const blocked = passport.blockers.length > 0;
  return (
    <div className="kpis">
      <Kpi label="Where it stands" value={position && position.known ? words(position.value) : 'not recorded'}
           tone={blocked ? 'danger' : 'good'} note={blocked ? `${count(passport.blockers.length)} thing(s) stopping it` : 'nothing is stopping it'} />
      <Kpi label="Days held" value={open ? count(open.daysHeld) : '—'} tone={open ? 'primary' : 'empty'}
           note={open ? `out with ${open.customerName ?? 'a customer'}` : 'not out with anybody'} />
      <Kpi label="Its periodic test" value={test && test.known ? words(test.value) : 'unknown'}
           tone={test?.value === 'in_date' ? 'good' : test?.known ? 'danger' : 'empty'}
           note={test?.sourceDate ? `since ${date(test.sourceDate)}` : 'nothing recorded has ever said'} />
      <Kpi label="Movements" value={count(passport.movements.page.total)} tone="primary"
           note={`${count(passport.trips.page.total)} trip(s) · rent ${rent?.value === 'running' ? 'running' : 'not running'}`} />
    </div>
  );
}

/**
 * THE STICKER ITSELF — a 50 x 25 mm thermal label: the QR, the CTS number, and
 * what the vessel carries. Drawn from the same `qrModules` the printed sheet
 * uses (`src/documents/qr.ts`), so what is on the screen and what comes out of
 * the printer are the same squares.
 */
function Sticker({ identity, modules, mark }: {
  identity: PassportData['identity']; modules: readonly string[]; mark: string | null;
}) {
  const n = modules.length;
  return (
    <div className="sticker" role="img" aria-label={`Label for ${identity.internalId}`}>
      <svg className="sticker-qr" viewBox={`0 0 ${n} ${n}`} shapeRendering="crispEdges" aria-hidden="true">
        <rect width={n} height={n} fill="#fff" />
        {modules.flatMap((row, y) => Array.from(row).map((bit, x) => (bit === '1'
          ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="#000" /> : null)))}
      </svg>
      <div className="sticker-side">
        {/* THE COMPANY MARK, as CTS1's label carries it. Through a data: URI in an
          * <img> on purpose: an SVG is a document and a document can carry script,
          * and script does not run in an image. 0177 refuses an executable mark at
          * the column as well — this is the second wall, not the only one. */}
        {mark ? <img className="sticker-mark" alt="" src={`data:image/svg+xml;utf8,${encodeURIComponent(mark)}`} /> : null}
        <div className="sticker-id" data-long={identity.internalId.length > 9 ? 'true' : 'false'}>{identity.internalId}</div>
        <div className="sticker-what">{words(identity.gasService).toUpperCase()}{identity.waterCapacityL ? ` · ${Number(identity.waterCapacityL)}L` : ''}</div>
      </div>
    </div>
  );
}

/**
 * THE PRINT VIEW. The label at its true size with everything else hidden, so
 * "Print" on a thermal printer puts out the sticker and nothing around it.
 */
function StickerPrint({ identity, modules, mark, onClose }: {
  identity: PassportData['identity']; modules: readonly string[]; mark: string | null; onClose: () => void;
}) {
  return (
    <div className="sticker-print" role="dialog" aria-label="Print this sticker">
      <div className="sticker-print-bar no-print">
        <Button onClick={onClose}>Close</Button>
        <Button kind="primary" onClick={() => globalThis.print?.()}>Print</Button>
      </div>
      <div className="sticker-print-stage">
        <div className="k no-print">Print preview · 50 × 25 mm thermal label · shown at 3× scale</div>
        <div className="sticker-print-label"><Sticker identity={identity} modules={modules} mark={mark} /></div>
        <p className="muted no-print" style={{ maxWidth: 520 }}>
          Set the paper size to 50 × 25 mm and the scale to 100%. In Safari, turn on
          “Print backgrounds” as well, or the black band under the number comes out empty.
          Chrome, Edge and Firefox take the size from the page itself.
        </p>
      </div>
    </div>
  );
}

/**
 * WHAT IS BLOCKED, WHAT IS STILL ALLOWED, AND WHO LIFTS IT.
 *
 * Every word here comes from the server, which got it from the one safety matrix. The
 * screen chooses nothing: if it did, it would be the fourth private copy of the rules the
 * project has already paid for three times (A01–A03).
 */
export function Blockers({ passport }: { passport: PassportData }) {
  const blockers = passport.blockers;
  if (blockers.length === 0) {
    return (
      <Card title="Nothing is stopping it" className="passport-blockers">
        <p className="good-line">Every ordinary act is available: {passport.stillAllowed.map((a) => ACTION_WORDS[a] ?? words(a)).join(', ')}.</p>
      </Card>
    );
  }
  return (
    <Card title={`${count(blockers.length)} ${blockers.length === 1 ? 'thing is' : 'things are'} stopping this cylinder`}
          className="passport-blockers" sub="what is blocked, what is still allowed, and who can resolve it">
      <ul className="blocker-list">
        {blockers.map((b) => (
          <li key={b.reason} className="blocker">
            <div className="blocker-head"><Pill tone="danger">{words(b.reason)}</Pill></div>
            <p className="blocker-says">{b.says}</p>
            <div className="blocker-grid">
              <div><div className="k">Blocked</div><div className="v">{b.blocks.map((a) => ACTION_WORDS[a] ?? words(a)).join(', ')}</div></div>
              <div><div className="k">Who can resolve it</div><div className="v">{b.resolvedBy}</div></div>
            </div>
            {b.link ? <p><a href={b.link}>Go and deal with it</a></p> : null}
          </li>
        ))}
      </ul>
      <p className="still-allowed">
        {passport.stillAllowed.length === 0
          ? 'Nothing ordinary may be done with it. A safety movement and recording a laboratory verdict are never blocked.'
          : <>Still allowed: {passport.stillAllowed.map((a) => ACTION_WORDS[a] ?? words(a)).join(', ')}.</>}
      </p>
      {passport.holds.filter((h) => h.open).map((h) => (
        <p key={h.holdId} className="muted">Hold — {words(h.kind)}: {h.reason} (opened {when(h.openedAt)}).</p>
      ))}
    </Card>
  );
}

/** What each act is called in front of a person. The server names the acts; this names them in English. */
const ACTION_WORDS: Record<string, string> = {
  fill: 'putting gas in it',
  ordinary_supply: 'sending it to a customer',
  return_to_owner: 'handing it back to its owner',
  release_after_qc: 'releasing it after a check',
  record_qc: "recording the laboratory's verdict",
  safety_movement: 'moving it to a testing or repair area',
  historical_entry: 'writing down a movement that already happened',
};

/**
 * THE SEVEN FACTS, AS SEVEN TILES (ADR-008, D264–D267).
 *
 * `known: false` is shown as "unknown", greyed, with the reason beside it — not as a blank
 * and never as the plausible answer. 0074 wrote `unknown` into rows the old data could not
 * speak for, and a screen that printed "empty" over that would be inventing a fact about a
 * pressure vessel. Each tile also carries the date the fact was established, which is the
 * difference between "empty, verified this morning" and "empty, we think, since 2019".
 */
export function SevenFacts({ facts }: { facts: PassportData['facts'] }) {
  return (
    <Card title="Where it stands" sub="seven separate facts — none of them is derived from another"
          className="seven-facts">
      <div className="fact-grid">
        {facts.map((f) => (
          <div key={f.fact} className={f.known ? 'fact' : 'fact fact-unknown'}>
            <div className="k">{f.label}</div>
            <div className="v">{f.known ? words(f.value) : <span className="muted">{words(f.value)}</span>}</div>
            <div className="when">
              {f.known
                ? (f.sourceDate ? `since ${date(f.sourceDate)}` : 'no date recorded')
                : 'nothing recorded has ever said'}
            </div>
          </div>
        ))}
      </div>
      <p className="muted">
        “Available” is a conclusion drawn from all seven. This page does not draw it for you.
      </p>
    </Card>
  );
}

/** What we bought, and what is stamped on it. Detail, deliberately below the facts. */
function WhatItIs({ identity }: { identity: PassportData['identity'] }) {
  const row = (k: string, v: React.ReactNode) => <div><div className="k">{k}</div><div className="v">{v ?? '—'}</div></div>;
  return (
    <div className="grid two">
      <Card title="What it is">
        <div className="detail-grid">
          {row('Manufacturer', identity.manufacturer)}
          {row('Year of manufacture', identity.yearOfManufacture)}
          {row('Water capacity', identity.waterCapacityL === null ? null : `${identity.waterCapacityL} L`)}
          {row('Tare weight', identity.tareWeightKg === null ? null : `${identity.tareWeightKg} kg`)}
          {row('Valve', identity.valveSpec)}
          {row('Cap', identity.capType)}
          {row('Working pressure', identity.workingPressure)}
          {row('Test pressure', identity.testPressure)}
          {row('Replacement cost', <Money paise={identity.replacementCostPaise} />)}
        </div>
      </Card>
      <Card title="How it came to us">
        <div className="detail-grid">
          {row('Vendor', identity.purchaseVendor)}
          {row('Invoice', identity.purchaseInvoice)}
          {row('Bought on', identity.purchaseDate === null ? null : date(identity.purchaseDate))}
          {row('Lot', identity.purchaseLot)}
          {row('Registered in CTS', identity.registeredOn === null ? null : when(identity.registeredOn))}
          {row('Lifecycle', <Pill>{identity.lifecycle}</Pill>)}
        </div>
      </Card>
    </div>
  );
}

function TripsBook({ passport, onPage }: { passport: PassportData; onPage: (n: number) => void }) {
  const trips = passport.trips;
  return (
    <Card title="Every trip it has made" sub="who held it, from when, how it came back — and how far rent has been charged"
          className="passport-trips">
      <Register
        columns={[
          { key: 'customer', head: 'Customer', cell: (t: PassportData['trips']['rows'][number]) => <a href={`#/customers/${t.customerId}`}>{t.customerName}</a> },
          { key: 'out', head: 'Out', cell: (t: PassportData['trips']['rows'][number]) => <>{date(t.outDate)}{t.outChallan ? (t.outChallanId ? <a className="muted" href={`#/challans/${t.outChallanId}`}> · {t.outChallan}</a> : <span className="muted"> · {t.outChallan}</span>) : null}</> },
          { key: 'back', head: 'Back', cell: (t: PassportData['trips']['rows'][number]) => t.backDate ? <>{date(t.backDate)}{t.backChallan ? (t.backChallanId ? <a className="muted" href={`#/challans/${t.backChallanId}`}> · {t.backChallan}</a> : <span className="muted"> · {t.backChallan}</span>) : null}</> : <span className="muted">still out</span> },
          { key: 'held', head: 'Days held', right: true, cell: (t: PassportData['trips']['rows'][number]) => count(t.daysHeld) },
          { key: 'billed', head: 'Rent charged to', cell: (t: PassportData['trips']['rows'][number]) => t.billedUpTo ? date(t.billedUpTo) : <span className="muted">not yet</span> },
          { key: 'status', head: 'Status', cell: (t: PassportData['trips']['rows'][number]) => <Pill>{t.status}</Pill> },
        ]}
        rows={trips.rows} empty="This cylinder has never left the plant." />
      <Pager page={trips.page} onPage={onPage} />
    </Card>
  );
}

/**
 * WHAT HAS BEEN DONE TO IT — the operational history, in one chronology.
 *
 * Received, inspected, prepared, filled with which gas from which batch, checked, sent
 * home on which note. Five tables on the server, one list here, because the question a
 * person asks is chronological and five lists he has to interleave in his head is not an
 * answer.
 */
function WorkBook({ passport, onPage }: { passport: PassportData; onPage: (n: number) => void }) {
  const work = passport.work;
  return (
    <Card title="Work done on it" sub="received, inspected, prepared, filled, checked, sent home" className="passport-work">
      {work.rows.length === 0 ? <p className="muted">No work has been recorded against this cylinder.</p> : (
        <ul className="timeline">
          {work.rows.map((w) => (
            <li key={w.workId}>
              <strong>{w.kind}</strong>
              {w.what ? <> — {words(w.what)}</> : null}
              {w.jobId ? <> · <a href={`#/customers/${w.customerId ?? ''}`}>{w.customerName ?? `job ${w.jobId}`}</a></> : null}
              <div className="when">
                {date(w.eventDate)}
                {w.detail ? ` · ${w.detail}` : ''}
                {w.byName ? ` · by ${w.byName}` : ''}
                {w.documentNumber ? ` · ${w.documentNumber}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
      <Pager page={work.page} onPage={onPage} />
    </Card>
  );
}

/**
 * THE MOVEMENT BOOK. What HAPPENED to the vessel, oldest last, never edited.
 *
 * A voided line stays and is struck through (D171 — the register must be correctable, and
 * a correction is a new line). A line keyed after the day it describes is marked `late`,
 * because an entry made three weeks afterwards is ordinary and must be visible: the owner's
 * rule is that a historical event looks different from current truth.
 */
function MovementBook({ passport, onPage }: { passport: PassportData; onPage: (n: number) => void }) {
  const movements = passport.movements;
  return (
    <Card title="Every movement" sub="newest first — a correction is a new line, never an edit" className="passport-movements">
      {movements.rows.length === 0 ? <p className="muted">No movement has been recorded yet.</p> : (
        <ul className="timeline">
          {movements.rows.map((m) => (
            <li key={m.movementId} className={m.voided ? 'voided' : undefined}>
              <strong>{words(m.type)}</strong>
              {m.from || m.to ? <> — {words(m.from)} &rarr; {words(m.to)}</> : null}
              {/* Slice 4B — a transfer between yards says WHICH yards, and keeps saying it
                * after the vessel has moved on again. */}
              {m.fromYard || m.toYard ? <> <Pill>{`${m.fromYard ?? 'elsewhere'} → ${m.toYard ?? 'on the road'}`}</Pill></> : null}
              {m.voided ? <> <Pill tone="danger">voided</Pill></> : null}
              {m.recordedLate ? <> <Pill tone="testing">written down later</Pill></> : null}
              <div className="when">
                {date(m.eventDate)}
                {m.recordedLate ? ` · keyed ${when(m.enteredAt)}` : ''}
                {m.documentRef ? ` · ${m.documentRef}` : ''}
                {m.recordedByName ? ` · by ${m.recordedByName}` : ''}
                {m.voidedReason ? ` · ${m.voidedReason}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
      <Pager page={movements.page} onPage={onPage} />
    </Card>
  );
}

/**
 * THE IDENTITY BOOK (D9's fourth book, published by 0123).
 *
 * What somebody SAID about the cylinder: a corrected serial, a transfer of ownership, a
 * gas conversion and the PESO reference that authorised it. Different from the movement
 * book above and different from the audit trail below, and the page keeps the three apart
 * because they answer three different questions.
 */
function IdentityBook({ passport, onPage }: { passport: PassportData; onPage: (n: number) => void }) {
  const changes = passport.identityChanges;
  const summarise = (v: unknown): string => {
    if (v === null || v === undefined) return '—';
    if (typeof v !== 'object') return String(v);
    return Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => `${words(k)} ${val === null ? '—' : String(val)}`).join(', ');
  };
  return (
    <Card title="What was changed about it" sub="a corrected serial, a transfer, a conversion — and what authorised it"
          className="passport-identity">
      {changes.rows.length === 0 ? <p className="muted">Nothing about this cylinder has been changed since it was registered.</p> : (
        <ul className="timeline">
          {changes.rows.map((c) => (
            <li key={c.eventId}>
              <strong>{words(c.type)}</strong>
              {c.pesoReference ? <> <Pill>{`PESO ${c.pesoReference}`}</Pill></> : null}
              <div className="when">
                {date(c.eventDate)} · {summarise(c.oldValue)} &rarr; {summarise(c.newValue)}
                {c.recordedByName ? ` · by ${c.recordedByName}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
      <Pager page={changes.page} onPage={onPage} />
    </Card>
  );
}

/** THE PAPER. Every document that names this cylinder, and WHY it is on this passport. */
function PaperBook({ passport, onPage }: { passport: PassportData; onPage: (n: number) => void }) {
  const documents = passport.documents;
  return (
    <Card title="Paperwork and evidence" sub="the challans it travelled on, and its own certificates" className="passport-paper">
      <Register
        columns={[
          { key: 'kind', head: 'Document', cell: (d: PassportData['documents']['rows'][number]) => d.link ? <a href={d.link}>{words(d.kind)}</a> : words(d.kind) },
          { key: 'number', head: 'Number', cell: (d: PassportData['documents']['rows'][number]) => d.number ?? '—' },
          { key: 'because', head: 'Why it is here', cell: (d: PassportData['documents']['rows'][number]) => d.because },
          { key: 'issued', head: 'Issued', cell: (d: PassportData['documents']['rows'][number]) => when(d.issuedAt) },
        ]}
        rows={documents.rows} empty="No paperwork names this cylinder yet." />
      <Pager page={documents.page} onPage={onPage} />
    </Card>
  );
}

/** WHAT WENT WRONG WITH IT, and what was decided about it. */
function DamageBook({ passport, onPage }: { passport: PassportData; onPage: (n: number) => void }) {
  const damage = passport.damage;
  return (
    <Card title="Damage recorded against it" sub="what was found, who was at fault, and how it was settled" className="passport-damage">
      <Register
        columns={[
          { key: 'type', head: 'What', cell: (d: PassportData['damage']['rows'][number]) => <>{words(d.damageType)}{d.severity ? ` · ${words(d.severity)}` : ''}</> },
          { key: 'challan', head: 'Found on', cell: (d: PassportData['damage']['rows'][number]) => d.challanId ? <a href={`#/challans/${d.challanId}`}>{d.challanNumber ?? `challan ${d.challanId}`}</a> : <span className="muted">—</span> },
          { key: 'fault', head: 'Fault', cell: (d: PassportData['damage']['rows'][number]) => d.fault ? words(d.fault) : '—' },
          { key: 'amount', head: 'Assessed', right: true, cell: (d: PassportData['damage']['rows'][number]) => <Money paise={d.resolvedAmountPaise ?? d.assessedAmountPaise ?? 0} /> },
          { key: 'status', head: 'Standing', cell: (d: PassportData['damage']['rows'][number]) => <>{d.supersededById ? <Pill tone="muted">{words(d.status)}</Pill> : <Pill>{words(d.status)}</Pill>}{d.supersededById ? <span className="muted"> superseded</span> : null}</> },
        ]}
        rows={damage.rows} empty="No damage has been recorded against this cylinder." />
      <Pager page={damage.page} onPage={onPage} />
    </Card>
  );
}

/**
 * WHAT THIS CYLINDER WEARS, AND THE ONE PLACE A REPLACEMENT IS ASKED FOR (Slice 3).
 *
 * A reprint is asked for HERE rather than on the labels screen, because this is where the
 * person is: he is holding a cylinder whose sticker has been scraped off with the paint, or
 * will not scan, and he is looking at its passport to find out what it is. Making him go to
 * another screen and search for it again is the navigation complaint this page exists to
 * answer.
 *
 * The reason is required and kept, because somebody months later will ask why one vessel
 * has three labels in its history.
 */
function LabelBook({ passport, cylinderId, session, onDone }: {
  passport: PassportData; cylinderId: number; session: Session; onDone: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [why, setWhy] = useState<'reprint' | 'restickered'>('restickered');
  const [stockId, setStockId] = useState('');
  const stocks = useAsk(() => api.view('labels.stocks', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const may = can(session, 'label.reprint');
  const labels = passport.labels;

  return (
    <Card title="Its label" sub="every one it has worn — a reprint is a new line, never a correction of the old one"
          className="passport-labels"
          actions={may && !passport.ended ? <Button onClick={() => setAsking((v) => !v)}>
            {asking ? 'Leave it' : 'Make another label'}</Button> : null}>
      {asking ? (
        <div className="toolbar">
          <Select name="relabel-stock" label="Stock" value={stockId} onChange={setStockId} placeholder="Choose the label stock"
                  options={(stocks.data?.rows ?? []).map((s) => ({ value: String(s.stockId), label: `${s.code} — ${s.name}` }))} />
          <Select name="relabel-why" label="Why" value={why} onChange={(v) => setWhy(v as 'reprint' | 'restickered')}
                  options={[{ value: 'restickered', label: 'The label came off or is unreadable' },
                            { value: 'reprint', label: 'The sheet was lost or never printed' }]} />
          <TextArea name="relabel-note" label="What happened" value={note} onChange={setNote}
                    placeholder="the drum was shot-blasted and the label came off with the paint" />
          <Button kind="primary" disabled={!stockId || !note.trim() || runner.busy}
                  onClick={() => { void runner.run((k) => api.command('label.reprint', {
                    stockId: Number(stockId), cylinderIds: [cylinderId], reason: why, note,
                  }, k ? { idempotencyKey: k } : {})).then(() => { setAsking(false); setNote(''); onDone(); }); }}>
            Prepare it
          </Button>
        </div>
      ) : null}
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Register
        columns={[
          { key: 'why', head: 'Why', cell: (l: PassportData['labels'][number]) => <>{words(l.reason)}{l.note ? <div className="muted">{l.note}</div> : null}</> },
          { key: 'run', head: 'Run', cell: (l: PassportData['labels'][number]) => <>#{l.batchId}<div className="muted">{words(l.batchStatus)}</div></> },
          // WHAT THE STICKER ON THE VESSEL ACTUALLY SAYS, recorded as printed. If the
          // hostname has since moved, this row still shows what is glued on out there.
          { key: 'says', head: 'What the code says', cell: (l: PassportData['labels'][number]) => <span className="serial">{l.qrPayload}</span> },
          { key: 'printed', head: 'Printed', cell: (l: PassportData['labels'][number]) => l.printedAt
              ? when(l.printedAt) : <span className="muted">not yet — nobody has said it came out</span> },
          { key: 'who', head: 'Asked for by', cell: (l: PassportData['labels'][number]) => <>{l.createdByName ?? '—'}<div className="muted">{when(l.createdAt)}</div></> },
        ]}
        rows={labels}
        empty="This cylinder has never been labelled. It is on the labels screen, waiting." />
      {labels.length === 0 ? <p className="muted"><a href="#/labels">Open the labels screen</a> to prepare a run.</p> : null}
    </Card>
  );
}

/**
 * THE MONEY, AS LINKS. Nothing is summed here.
 *
 * Payable, outstanding and on-account are three different figures (D161) and they belong
 * to the statement. A total computed on a passport is a total that will one day disagree
 * with the account, so this book links and does not add up.
 */
function MoneyBook({ passport }: { passport: PassportData }) {
  const c = passport.commercial;
  return (
    <>
      <Card title="What has been charged for it" sub="holding, work and damage — read from the account, never recalculated here"
            className="passport-money">
        <Register
          columns={[
            { key: 'type', head: 'Charge', cell: (x: PassportData['commercial']['charges'][number]) => <>{words(x.chargeType)}{x.voided ? <> <Pill tone="danger">voided</Pill></> : null}</> },
            { key: 'covers', head: 'Covers', cell: (x: PassportData['commercial']['charges'][number]) => x.coversFrom ? `${date(x.coversFrom)} — ${x.coversTo ? date(x.coversTo) : 'open'}` : (x.effectiveMonth ? date(x.effectiveMonth) : '—') },
            { key: 'what', head: 'Description', cell: (x: PassportData['commercial']['charges'][number]) => x.description ?? '—' },
            { key: 'amount', head: 'Amount', right: true, cell: (x: PassportData['commercial']['charges'][number]) => <Money paise={x.amountPaise} /> },
            { key: 'bill', head: 'On bill', cell: (x: PassportData['commercial']['charges'][number]) => x.billId ? <a href={`#/billing/${x.billId}`}>open it</a> : <span className="muted">not billed yet</span> },
          ]}
          rows={c.charges} empty="Nothing has been charged against this cylinder." />
        {c.more ? <p className="muted">Only the most recent twenty are shown. The customer's account has all of them.</p> : null}
      </Card>
      <div className="grid two">
        <Card title="Bills it appears on">
          <Register
            columns={[
              { key: 'number', head: 'Bill', cell: (x: PassportData['commercial']['bills'][number]) => <a href={`#/billing/${x.billId}`}>{x.number ?? `bill ${x.billId}`}</a> },
              { key: 'period', head: 'Period', cell: (x: PassportData['commercial']['bills'][number]) => x.periodStart ? `${date(x.periodStart)} — ${x.periodEnd ? date(x.periodEnd) : ''}` : '—' },
              { key: 'status', head: 'Standing', cell: (x: PassportData['commercial']['bills'][number]) => <Pill>{x.status}</Pill> },
              { key: 'total', head: 'Total', right: true, cell: (x: PassportData['commercial']['bills'][number]) => <Money paise={x.totalPaise} /> },
            ]}
            rows={c.bills} empty="It is not on any bill yet." />
        </Card>
        <Card title="Orders it was sent against">
          <Register
            columns={[
              { key: 'order', head: 'Order', cell: (x: PassportData['commercial']['orders'][number]) => <a href={`#/orders/${x.orderId}`}>{x.customerReference ?? `order ${x.orderId}`}</a> },
              { key: 'customer', head: 'Customer', cell: (x: PassportData['commercial']['orders'][number]) => <a href={`#/customers/${x.customerId}`}>{x.customerName ?? `customer ${x.customerId}`}</a> },
              { key: 'asked', head: 'Asked for', cell: (x: PassportData['commercial']['orders'][number]) => x.requestedDate ? date(x.requestedDate) : '—' },
              { key: 'status', head: 'Standing', cell: (x: PassportData['commercial']['orders'][number]) => <Pill>{x.status}</Pill> },
            ]}
            rows={c.orders} empty="It has never gone out against an order." />
        </Card>
      </div>
    </>
  );
}

/**
 * WHAT WAS CHANGED ON THE PASSPORT, AND WHY. The movements above are what
 * HAPPENED to the cylinder; this is what somebody SAID about it — a corrected
 * serial, a transfer, a conversion. They are different questions and the screen
 * keeps them apart (ADR-008).
 */
function ChangeHistory({ cylinderId }: { cylinderId: number }) {
  const asked = useAsk(() => api.view('audit.entity', { entity: `registry.cylinders:${cylinderId}`, pageSize: 20 }), [cylinderId], MESSAGES);
  if (asked.loading) return <Card title="What was changed about it"><Loading what="the record" /></Card>;
  if (asked.error) return <Card title="What was changed about it"><Banner kind="warn" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const rows = asked.data!.rows;
  return (
    <Card title="What was changed about it" sub="who changed the passport, when, and why">
      {rows.length === 0 ? <p className="muted">Nothing has been changed since it was registered.</p> : (
        <ul className="timeline">
          {rows.map((a) => (
            <li key={a.auditId}>
              <strong>{actionWords(a.action)}</strong> by {a.actorName ?? `user ${a.actorId}`}
              {a.reason ? <> — {a.reason}</> : null}
              <div className="when">{when(a.at)}</div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * A CORRECTION IS NOT A MOVEMENT. What is fixed here is what the cylinder IS —
 * a serial mistyped at registration, a tare weight measured properly — and the
 * ledger of where it has been is untouched. The reason is required because a
 * serial that changes is a serial somebody else has already written on paper.
 */
function CorrectCylinder({ cylinder, onDone }: {
  cylinder: { cylinderId: number; serialNo: string; waterCapacityL: number | string | null; replacementCostPaise: number | null; manufacturer?: string | null; yearOfManufacture?: number | null };
  onDone: () => void;
}) {
  const [serialNo, setSerialNo] = useState(cylinder.serialNo);
  const [capacity, setCapacity] = useState(cylinder.waterCapacityL === null || cylinder.waterCapacityL === undefined ? '' : String(cylinder.waterCapacityL));
  const [cost, setCost] = useState(cylinder.replacementCostPaise === null || cylinder.replacementCostPaise === undefined ? '' : (cylinder.replacementCostPaise / 100).toFixed(2));
  const [maker, setMaker] = useState(cylinder.manufacturer ?? '');
  const [year, setYear] = useState(cylinder.yearOfManufacture ? String(cylinder.yearOfManufacture) : '');
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);

  const costPaise = cost.trim() ? rupeesToPaise(cost) : undefined;
  const changes = {
    ...(serialNo.trim() !== cylinder.serialNo ? { serialNo: serialNo.trim() } : {}),
    ...(capacity.trim() && capacity.trim() !== String(cylinder.waterCapacityL ?? '') ? { waterCapacityL: Number(capacity) } : {}),
    ...(costPaise !== undefined && costPaise !== null && costPaise !== cylinder.replacementCostPaise ? { replacementCostPaise: costPaise } : {}),
    ...(maker.trim() && maker.trim() !== (cylinder.manufacturer ?? '') ? { manufacturer: maker.trim() } : {}),
    ...(year.trim() && Number(year) !== cylinder.yearOfManufacture ? { yearOfManufacture: Number(year) } : {}),
  };
  const nothingChanged = Object.keys(changes).length === 0;

  async function submit() {
    const ok = await runner.run(() => api.command('cylinder.correct', { cylinderId: cylinder.cylinderId, changes, reason: reason.trim() }));
    if (ok) onDone();
  }

  return (
    <Card title="Correct the passport" sub="what the cylinder is — not where it has been, which is never edited">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {costPaise === null ? <Banner kind="warn" title="The replacement cost is not money." detail="Rupees, with at most two decimals." /> : null}
      <div className="grid two">
        <div>
          <Text name="fix-serial" label="Serial number" value={serialNo} onChange={setSerialNo} required />
          <Text name="fix-capacity" label="Water capacity (L)" type="number" value={capacity} onChange={setCapacity} />
          <Text name="fix-cost" label="Replacement cost (₹)" value={cost} onChange={setCost} help="what it would cost to replace if it never comes back" />
        </div>
        <div>
          <Text name="fix-maker" label="Manufacturer" value={maker} onChange={setMaker} />
          <Text name="fix-year" label="Year of manufacture" type="number" value={year} onChange={setYear} />
          <TextArea name="fix-reason" label="Why it is being corrected" value={reason} onChange={setReason} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim() || nothingChanged || costPaise === null}>
        {runner.busy ? 'Saving…' : nothingChanged ? 'Nothing has changed' : 'Save the correction'}
      </Button>
    </Card>
  );
}

/** Ownership moves on a DATE, because the holding it is charged under depends on who owned it that day. */
function TransferOwner({ cylinderId, ownerKind, onDone }: { cylinderId: number; ownerKind: string; onDone: () => void }) {
  const [toOwnerKind, setToOwnerKind] = useState(ownerKind === 'company' ? 'customer' : 'company');
  const [toCustomerId, setToCustomerId] = useState('');
  const [onDate, setOnDate] = useState(today());
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);

  const needsCustomer = toOwnerKind === 'customer';

  async function submit() {
    const ok = await runner.run(() => api.command('cylinder.transferOwner', {
      cylinderId, toOwnerKind: toOwnerKind === 'customer' ? 'customer' : 'company',
      ...(needsCustomer ? { toCustomerId: Number(toCustomerId) } : {}),
      onDate, reason: reason.trim(),
    }));
    if (ok) onDone();
  }

  return (
    <Card title="Transfer ownership" sub="who owns the cylinder — not who is holding it today">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Select name="to-owner" label="Owned from now by" value={toOwnerKind} onChange={setToOwnerKind}
                  options={[{ value: 'company', label: 'Us' }, { value: 'customer', label: 'A customer' }]} />
          {needsCustomer ? (
            <CustomerPickerById name="to-customer" label="Which customer" customerId={toCustomerId}
                                onPick={(v) => setToCustomerId(v)} placeholder="Choose a customer" />
          ) : null}
        </div>
        <div>
          <Text name="transfer-date" label="From which date" type="date" value={onDate} onChange={setOnDate} required />
          <TextArea name="transfer-reason" label="Why it is transferring" value={reason} onChange={setReason} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim() || (needsCustomer && !toCustomerId)}>
        {runner.busy ? 'Recording…' : 'Record the transfer'}
      </Button>
    </Card>
  );
}

/**
 * CONVERTING THE GAS SERVICE is not a preference — it is a regulated change,
 * and the PESO reference is the proof it was allowed. The command requires it,
 * and so does this form: a conversion with no paper behind it is the one the
 * inspector asks about.
 */
function ConvertGas({ cylinderId, from, onDone }: { cylinderId: number; from: string; onDone: () => void }) {
  const [toGas, setToGas] = useState('');
  const [peso, setPeso] = useState('');
  const [onDate, setOnDate] = useState(today());
  const runner = useRun(MESSAGES);

  async function submit() {
    const ok = await runner.run(() => api.command('cylinder.convertGas', { cylinderId, toGas: toGas.trim(), pesoReference: peso.trim(), onDate }));
    if (ok) onDone();
  }

  return (
    <Card title={`Convert from ${words(from)}`} sub="the PESO reference is what makes this legal — it is required">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Select name="to-gas" label="Converted to" value={toGas} onChange={setToGas} placeholder="Choose the gas"
                  options={[{ value: 'oxygen', label: 'Oxygen' }, { value: 'acetylene', label: 'Acetylene' }, { value: 'nitrogen', label: 'Nitrogen' }, { value: 'argon', label: 'Argon' }, { value: 'co2', label: 'CO₂' }]} />
          <Text name="peso" label="PESO reference" value={peso} onChange={setPeso} required help="the approval that permits this conversion" />
        </div>
        <div>
          <Text name="convert-date" label="Converted on" type="date" value={onDate} onChange={setOnDate} required />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !toGas || !peso.trim()}>
        {runner.busy ? 'Recording…' : 'Record the conversion'}
      </Button>
    </Card>
  );
}

/** The end of a cylinder's life is final: it leaves the register, and it can no longer be sent anywhere. */
function EndLife({ cylinderId, internalId, onDone }: { cylinderId: number; internalId: string; onDone: () => void }) {
  const [ending, setEnding] = useState('scrapped');
  const [onDate, setOnDate] = useState(today());
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);

  async function submit() {
    const ok = await runner.run(() => api.command('cylinder.endLife', {
      cylinderId, ending: ending === 'lost' ? 'lost' : 'scrapped', onDate, reason: reason.trim(),
    }));
    if (ok) onDone();
  }

  return (
    <Card title={`End the life of ${internalId}`} sub="this cannot be undone — the cylinder can never be sent out again">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="warn" title="A cylinder ended here stops counting as stock and stops being chargeable."
              detail="If it is only missing and may come back, say so in the reason and record it as lost rather than scrapped." />
      <div className="grid two">
        <div>
          <Select name="ending" label="What happened to it" value={ending} onChange={setEnding}
                  options={[{ value: 'scrapped', label: 'Scrapped' }, { value: 'lost', label: 'Lost' }]} />
          <Text name="end-date" label="On which date" type="date" value={onDate} onChange={setOnDate} required />
        </div>
        <div>
          <TextArea name="end-reason" label="What happened" value={reason} onChange={setReason} />
        </div>
      </div>
      <Button kind="danger" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim()}>
        {runner.busy ? 'Recording…' : `Record it as ${ending}`}
      </Button>
    </Card>
  );
}

/**
 * REGISTRATION, ONE OR A LOT. A plant does not buy one cylinder; it takes
 * delivery of forty with consecutive serials, and typing them one at a time is
 * how three of them end up with the wrong gas. The lot shares everything
 * except the serial — which is exactly the shape of the bulk command.
 */
function RegisterCylinder({ session }: { session: Session }) {
  const [lot, setLot] = useState(false);
  const [serial, setSerial] = useState('');
  const [serials, setSerials] = useState('');
  const [gas, setGas] = useState('oxygen');
  const [owner, setOwner] = useState('company');
  const [capacity, setCapacity] = useState('');
  const [startingState, setStartingState] = useState('empty_in_plant');
  // 13 Oct — the stamping is already on one of ours. The refusal said a branch
  // manager must record the duplicate; until now it named a door that did not
  // exist for the company's own fleet, so the person was simply stuck.
  const [duplicate, setDuplicate] = useState(false);
  const [why, setWhy] = useState('');
  const runner = useRun(MESSAGES);

  const typed = serials.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
  const unique = [...new Set(typed)];
  const repeated = typed.length - unique.length;

  const common = () => ({
    gasService: gas.trim(),
    ownerKind: (owner === 'customer' ? 'customer' : 'company') as 'company' | 'customer',
    ...(capacity.trim() ? { waterCapacityL: Number(capacity) } : {}),
    ...(startingState ? { startingState: startingState as 'empty_in_plant' | 'filled_in_plant' | 'at_owner' } : {}),
  });

  async function submit() {
    const ok = await runner.run((retryKey) => lot
      ? api.command('cylinder.bulkRegister', { serials: unique, common: common() }, retryKey ? { idempotencyKey: retryKey } : {})
      : duplicate
        ? api.command('cylinder.registerDuplicate', { serialNo: serial.trim(), reason: why.trim(), ...common() },
                      retryKey ? { idempotencyKey: retryKey } : {})
        : api.command('cylinder.register', { serialNo: serial.trim(), ...common() }));
    if (ok) go('#/cylinders');
  }
  // the refusal the owner met: offered the way through, to whoever may take it
  const clash = !lot && runner.error?.code === 'serial.duplicate';

  return (
    <Page title={lot ? 'Register a lot of cylinders' : 'Register a cylinder'}
          sub="A cylinder gets its permanent identity once, and keeps it for life."
          actions={<Button onClick={() => { setLot((v) => !v); setDuplicate(false); }}>{lot ? 'Just one' : 'A whole lot'}</Button>}>
      <Card>
        {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
        {clash || duplicate ? (
          can(session, 'job.resolve_contested_intake') ? (
            <div className="dup-path">
              <label className="checkline">
                <input type="checkbox" checked={duplicate} onChange={(e: { target: { checked: boolean } }) => setDuplicate(e.target.checked)} />
                <b>This really is a second cylinder stamped the same</b>
              </label>
              <p className="muted">
                Two of ours can carry one stamping — different makers, different series, a re-used block of
                numbers — and that is also what a mistyped digit looks like. Recording it is your decision, it
                is kept with your name on it, and the pair reads afterwards as one investigated stamping.
              </p>
              {duplicate ? (
                <TextArea name="dup-why" label="Why this is a second physical cylinder" value={why} onChange={setWhy}
                          placeholder="both are in front of me: 9215 on an oxygen 46.7 L from 2019, and this acetylene 46 L — the maker's block was re-used" />
              ) : null}
            </div>
          ) : (
            <Banner kind="info" title="Only a branch manager can record a duplicate stamping."
                    detail="Two cylinders carrying one stamping is a decision about which vessel is which, so it is kept with a name against it. Ask somebody who settles contested identities to register this one." />
          )
        ) : null}
        <div className="grid two">
          <div>
            {lot ? (
              <>
                <TextArea name="serials" label="Serial numbers" value={serials} onChange={setSerials}
                          help="one per line, or separated by commas — everything else below is the same for all of them" />
                <p className="muted">
                  {unique.length === 0 ? 'No serial typed yet.' : `${count(unique.length)} cylinder(s) will be registered.`}
                  {repeated > 0 ? ` ${count(repeated)} repeated serial(s) ignored.` : ''}
                </p>
              </>
            ) : (
              <Text name="serial" label="Serial number" value={serial} onChange={setSerial} required autoFocus />
            )}
            <Select name="gas" label="Gas" value={gas} onChange={setGas}
                    options={[{ value: 'oxygen', label: 'Oxygen' }, { value: 'acetylene', label: 'Acetylene' }, { value: 'nitrogen', label: 'Nitrogen' }, { value: 'argon', label: 'Argon' }, { value: 'co2', label: 'CO₂' }]} />
            <Text name="capacity" label="Water capacity (L)" type="number" value={capacity} onChange={setCapacity} />
          </div>
          <div>
            <Select name="owner" label="Owner" value={owner} onChange={setOwner}
                    options={[{ value: 'company', label: 'Ours' }, { value: 'customer', label: "A customer's" }]} />
            <Select name="starting" label="Starts as" value={startingState} onChange={setStartingState}
                    options={[{ value: 'empty_in_plant', label: 'Empty, in the plant' }, { value: 'filled_in_plant', label: 'Filled, in the plant' }, { value: 'at_owner', label: 'At its owner' }]} />
          </div>
        </div>
        <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || (lot ? unique.length === 0 : !serial.trim())}>
          {runner.busy ? 'Registering…' : lot ? `Register ${count(unique.length)} cylinder(s)` : 'Register it'}
        </Button>
      </Card>
    </Page>
  );
}

/**
 * BOOK 3 — WHAT HAS BEEN DONE TO THIS VESSEL, AND WHETHER IT IS FIT (DEF-168).
 *
 * The status shown is DERIVED, read fresh from the server against today's date. It is
 * not a column: a cylinder goes overdue because time passed, and a page that cached the
 * answer would be the defect this book was built to end.
 *
 * Corrections are SHOWN, struck through rather than hidden. A maintenance book with its
 * corrections removed is a tidied one, and an investigation cannot use it.
 */
// `testing` is the palette's amber — the tone this system already uses for a cylinder
// that needs attention rather than one that is wrong (D260: the visual language is CTS1's).
const TEST_TONE: Record<string, 'good' | 'testing' | 'danger' | 'muted'> = {
  current: 'good', due_soon: 'testing', overdue: 'danger', failed: 'danger',
  condemned: 'danger', destroyed: 'muted', unknown: 'testing',
};
const TEST_WORDS: Record<string, string> = {
  current: 'test in date', due_soon: 'test due soon', overdue: 'TEST OVERDUE',
  failed: 'FAILED ITS TEST', condemned: 'CONDEMNED', destroyed: 'destroyed',
  unknown: 'no test on record',
};
const EVIDENCE_WORDS: Record<string, string> = {
  performed: 'we tested it', certificate_sighted: 'certificate seen', stamp_observed: 'stamping read',
};

function MaintenanceBook({ cylinderId, session, onDone }: {
  cylinderId: number; session: Session; onDone: () => void;
}) {
  const asked = useAsk(() => api.view('maintenance.book', { cylinderId }), [cylinderId], MESSAGES);
  const [adding, setAdding] = useState(false);
  const [condemning, setCondemning] = useState(false);
  const [correcting, setCorrecting] = useState<number | null>(null);
  const [attaching, setAttaching] = useState<number | null>(null);
  if (asked.loading) return <Card title="Maintenance, testing and repair"><Loading what="the cylinder's maintenance book" /></Card>;
  const b = asked.data;
  // A BOOK THAT FAILED TO LOAD MUST NOT LOOK LIKE A CYLINDER WITH NO HISTORY. Returning
  // null here hid the error completely: the card vanished, and a page with no
  // maintenance section reads as "nothing has ever happened to this vessel" — which for
  // a fitness-to-fill question is the most dangerous wrong answer available.
  if (!b) {
    return (
      <Card title="Maintenance, testing and repair"
            actions={<Button small onClick={() => asked.reload()}>Try again</Button>}>
        <Banner kind="error"
                title={asked.error?.title ?? 'The maintenance book could not be read.'}
                detail={`${asked.error?.detail ?? 'The server did not answer for this cylinder\'s testing history.'} `
                  + 'Do NOT read this as a cylinder with no history — nothing is known either way until it loads.'}
                {...(asked.error?.reference ? { reference: asked.error.reference } : {})} />
      </Card>
    );
  }
  const may = can(session, 'cylinder.record_maintenance');
  const status = b.testStatus;

  return (
    <Card title="Maintenance, testing and repair"
          sub="the third of the four books every cylinder carries"
          actions={<>
            <Pill tone={TEST_TONE[status] ?? 'muted'}>{TEST_WORDS[status] ?? status}</Pill>
            {may ? <Button small onClick={() => setAdding((v) => !v)}>{adding ? 'Leave it' : 'Record a test or repair'}</Button> : null}
            {can(session, 'cylinder.condemn') && status !== 'destroyed'
              ? <Button small kind="danger" onClick={() => setCondemning((v) => !v)}>
                  {condemning ? 'Leave it' : status === 'condemned' ? 'Record its destruction' : 'Condemn it'}
                </Button>
              : null}
          </>}>

      {/*
        * A06 — DESTROYED AND STILL ON THE BOOKS. First, because it is the one thing on this
        * page that somebody has to act on.
        *
        * A passport that said "destroyed" and nothing else read as a finished matter. It is
        * not finished: the vessel is gone and the register still counts it, because custody
        * could not honestly end the life while it was out with a customer or on a trip whose
        * receipt challan has not been found. CTS will not invent a return date (D174), so a
        * person must settle the trip — and until this banner existed, the only place that
        * said so was a queue nobody opens from here.
        */}
      {b.pendingFinalisation
        ? <Banner kind="warn"
                  title={`Destroyed on ${date(b.pendingFinalisation.destroyedOn)} and still counted in the active fleet`
                    + (b.pendingFinalisation.daysUnfinalised > 0 ? ` — ${b.pendingFinalisation.daysUnfinalised} days` : '')}
                  detail={`The vessel is gone; its life has not been ended, because `
                    + (b.pendingFinalisation.openTripId !== null
                        ? `trip #${b.pendingFinalisation.openTripId} is still ${b.pendingFinalisation.openTripStatus ?? 'open'}`
                        : b.pendingFinalisation.physicalPosition !== null
                          ? `it is recorded as ${b.pendingFinalisation.physicalPosition}`
                          : 'custody could not be settled')
                    + '. Settle the trip — confirm with the customer what became of it — and then finish the destruction '
                    + 'on the Cylinders screen. CTS will not invent a return date to close it for you.'} />
        : null}

      {status === 'overdue'
        ? <Banner kind="error" title={`Overdue for its periodic test${b.nextTestDue ? ` — it was due ${date(b.nextTestDue)}` : ''}.`}
                  detail="It may not be filled until the test is done and recorded. It went overdue because the date passed, not because anybody changed it." />
        : null}
      {status === 'failed'
        ? <Banner kind="error" title={`It FAILED its periodic test${b.lastFailedOn ? ` on ${date(b.lastFailedOn)}` : ''}.`}
                  detail="A cylinder that fails its test is scrapped. A later pass does not return it to service, and a passing gas check says nothing about the vessel. If the failure was recorded in error it can be corrected on evidence, by somebody holding that authority; otherwise the route out is condemnation." />
        : null}
      {status === 'unknown'
        ? <Banner kind="warn" title="No test we can rely on."
                  detail={b.lastTestOn
                    ? `A test on ${date(b.lastTestOn)} is on the book with no expiry recorded — a date with no promise attached is not a promise.`
                    : 'Nothing has ever been recorded against this cylinder. That is not the same as being in date.'} />
        : null}

      <div className="detail-grid">
        <div><div className="k">Last test</div><div className="v">{b.lastTestOn ? date(b.lastTestOn) : '—'}</div></div>
        <div><div className="k">Next due</div><div className="v">{b.nextTestDue ? date(b.nextTestDue) : 'not recorded'}</div></div>
        <div><div className="k">On what evidence</div><div className="v">{b.lastTestEvidence ? EVIDENCE_WORDS[b.lastTestEvidence] ?? b.lastTestEvidence : '—'}</div></div>
        <div><div className="k">Condemned</div><div className="v">{b.condemnedOn ? date(b.condemnedOn) : 'no'}</div></div>
      </div>

      {adding ? <RecordMaintenance cylinderId={cylinderId} onDone={() => { setAdding(false); asked.reload(); onDone(); }} /> : null}
      {condemning ? <Condemn cylinderId={cylinderId} alreadyCondemned={status === 'condemned'}
                             onDone={() => { setCondemning(false); asked.reload(); onDone(); }} /> : null}
      {correcting !== null
        ? <CorrectMaintenance eventId={correcting} session={session}
                              event={b.events.find((e) => e.eventId === correcting)}
                              papers={b.papers ?? []}
                              onDone={() => { setCorrecting(null); asked.reload(); onDone(); }} />
        : null}
      {attaching !== null
        ? <AttachPaper eventId={attaching}
                       onDone={() => { setAttaching(null); asked.reload(); }} />
        : null}

      {b.events.length === 0
        ? <Banner kind="info" title="The book is empty."
                  detail="Record the test date stamped on the cylinder, or the certificate if you are holding one — they are not the same thing and the book says which." />
        : <Register
            empty="nothing recorded"
            columns={[
              { key: 'when', head: 'When', cell: (e) => <>{date(e.performedOn)}{e.enteredLate ? <div className="muted">entered later</div> : null}</> },
              { key: 'what', head: 'What', cell: (e) => <>{words(e.kind)}{e.superseded ? <Pill tone="muted">corrected</Pill> : null}</> },
              { key: 'outcome', head: 'Outcome', cell: (e) => <Pill tone={e.outcome === 'failed' ? 'danger' : e.outcome === 'passed' ? 'good' : 'muted'}>{e.outcome}</Pill> },
              { key: 'evidence', head: 'On what evidence', cell: (e) => EVIDENCE_WORDS[e.evidence] ?? e.evidence },
              { key: 'until', head: 'Good until', cell: (e) => e.validUntil ? date(e.validUntil) : <span className="muted">not stated</span> },
              { key: 'paper', head: 'Certificate', cell: (e) => e.certificateRef ?? <span className="muted">—</span> },
              { key: 'who', head: 'Recorded by', cell: (e) => <>{e.recordedByName ?? '—'}<div className="muted">{e.reason}</div></> },
              { key: 'paper', head: 'The paper', cell: (e) => {
                  const mine = (b.papers ?? []).filter((p) => p.eventId === e.eventId);
                  return <>
                    {mine.map((p) => (
                      <div key={p.attachmentId}>
                        <a href={`/documents/maintenance-papers/${p.attachmentId}`}
                           download={p.filename}>{p.filename}</a>
                        <div className="muted">{words(p.category)}</div>
                      </div>
                    ))}
                    {can(session, 'cylinder.store_maintenance_paper')
                      ? <Button small onClick={() => setAttaching(e.eventId)}>Attach</Button>
                      : null}
                  </>;
                } },
              { key: 'fix', head: '', cell: (e) => (!e.superseded && can(session, 'cylinder.correct_maintenance')
                  ? <Button small onClick={() => setCorrecting(e.eventId)}>Correct it</Button>
                  : null) },
            ] as readonly Column<(typeof b.events)[number]>[]}
            rows={b.events} />}
    </Card>
  );
}

function RecordMaintenance({ cylinderId, onDone }: { cylinderId: number; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [kind, setKind] = useState('periodic_test');
  // NOT DEFAULTED. The whole book rests on the difference between a test we performed
  // and a date somebody read off a neck ring, and a pre-selected "performed / passed"
  // is the system guessing the two most consequential answers on the form. Both start
  // empty and the button stays disabled until a person chooses.
  const [evidence, setEvidence] = useState('');
  const [outcome, setOutcome] = useState('');
  const [performedOn, setPerformedOn] = useState(today());
  const [validUntil, setValidUntil] = useState('');
  const [testStation, setTestStation] = useState('');
  const [certificateRef, setCertificateRef] = useState('');
  const [reason, setReason] = useState('');

  async function submit() {
    const ok = await runner.run((retryKey) => api.command('cylinder.recordMaintenance', {
      cylinderId, kind: kind as never, evidence: evidence as never, outcome: outcome as never,
      performedOn, reason: reason.trim(),
      ...(validUntil ? { validUntil } : {}),
      ...(testStation.trim() ? { testStation: testStation.trim() } : {}),
      ...(certificateRef.trim() ? { certificateRef: certificateRef.trim() } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {/* The distinction the whole book rests on, said where the choice is made. */}
      <Banner kind="info" title="A date you read is not a test you did."
              detail="Reading a date stamped on the neck ring records what somebody else says they did. Holding the certificate is better. Performing the test is best. Say which — nothing here guesses, and a stamping cannot carry a certificate number." />
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Select name="mx-kind" label="What happened" value={kind} onChange={setKind}
                options={[{ value: 'periodic_test', label: 'Periodic (hydrostatic) test' },
                          { value: 'visual_inspection', label: 'Visual inspection' },
                          { value: 'valve_replacement', label: 'Valve replacement' },
                          { value: 'repair', label: 'Repair' }]} />
        <Select name="mx-evidence" label="On what evidence" value={evidence} onChange={setEvidence}
                placeholder="Choose — this is never assumed"
                options={[{ value: 'performed', label: 'We did it, or had it done' },
                          { value: 'certificate_sighted', label: 'We have seen the certificate' },
                          { value: 'stamp_observed', label: 'We read the stamping only' }]} />
        <Select name="mx-outcome" label="Outcome" value={outcome} onChange={setOutcome}
                placeholder="Choose"
                options={[{ value: 'passed', label: 'Passed' }, { value: 'failed', label: 'Failed' },
                          { value: 'done', label: 'Done' }, { value: 'not_applicable', label: 'Not applicable' }]} />
        <Text name="mx-on" label="Performed on" value={performedOn} onChange={setPerformedOn} />
        {outcome === 'passed'
          ? <Text name="mx-until" label="Good until (leave blank if not stated)" value={validUntil} onChange={setValidUntil} />
          : null}
        {evidence !== 'stamp_observed'
          ? <Text name="mx-cert" label="Certificate reference" value={certificateRef} onChange={setCertificateRef} />
          : null}
        <Text name="mx-station" label="Test station" value={testStation} onChange={setTestStation} />
        <Text name="mx-reason" label="Why" value={reason} onChange={setReason} />
        <Button kind="primary" disabled={runner.busy || !reason.trim() || !performedOn || !evidence || !outcome}
                onClick={() => { void submit(); }}>Record it</Button>
      </div>
      {!evidence || !outcome
        ? <p className="muted">Say what the evidence is and what the outcome was — neither is assumed.</p>
        : null}
      {outcome === 'passed' && !validUntil
        ? <p className="muted">No expiry: the book will record the date and read the cylinder as <b>no test we can rely on</b> — which is the honest answer, not a fault.</p>
        : null}
    </div>
  );
}

/**
 * A CORRECTION APPENDS. The original stays on the book exactly as it was written and is
 * marked corrected; this records what it should have said. Nothing is edited, because
 * "what did we believe, and when" is what an investigation asks.
 */
function CorrectMaintenance({ eventId, event, papers, session, onDone }: {
  eventId: number;
  event: { kind: string; outcome: string } | undefined;
  papers: { attachmentId: number; eventId: number; filename: string; category: string }[];
  session: Session;
  onDone: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [kind, setKind] = useState('periodic_test');
  const [evidence, setEvidence] = useState('');
  const [outcome, setOutcome] = useState('');
  const [performedOn, setPerformedOn] = useState(today());
  const [validUntil, setValidUntil] = useState('');
  const [reason, setReason] = useState('');
  const [evidenceId, setEvidenceId] = useState('');

  /*
   * WOULD THIS CORRECTION RETURN THE VESSEL TO SERVICE? (DEF-172/DEF-173)
   *
   * The server decides for certain, by writing the correction and re-reading the derived
   * state — it is the only way to catch every route, including changing the event's KIND.
   * The screen cannot do that, and does not pretend to: it shows the warning and asks for
   * the evidence whenever the record being corrected is a FAILED test or a condemnation,
   * which is the set of cases where a clearing is possible at all. Asking for evidence
   * that turns out not to be needed costs a moment; not asking costs a refusal after the
   * person has typed everything.
   */
  const couldClear = event !== undefined
    && ((event.kind === 'periodic_test' && event.outcome === 'failed') || event.kind === 'condemnation');
  const mayClear = can(session, 'cylinder.clear_unfit_finding');
  const ours = papers.filter((p) => p.eventId === eventId);

  async function submit() {
    const ok = await runner.run((retryKey) => api.command('cylinder.correctMaintenance', {
      eventId, reason: reason.trim(),
      ...(evidenceId ? { evidenceAttachmentId: Number(evidenceId) } : {}),
      replacement: {
        kind: kind as never, evidence: evidence as never, outcome: outcome as never,
        performedOn, reason: reason.trim(), ...(validUntil ? { validUntil } : {}),
      },
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title="The first record stays on the book."
              detail="A correction is a new entry that names the one it replaces. Nothing is erased — the original keeps its own words, marked corrected." />

      {couldClear && !mayClear
        ? <Banner kind="warn" title="You may correct this record, but not clear its finding."
                  detail="Changing a failed test or a condemnation into anything else returns the cylinder to service, and that needs an authority you do not hold. A correction that leaves the finding standing — a wrong date, a wrong station — is yours to make." />
        : null}
      {couldClear && mayClear
        ? <Banner kind="warn" title="This correction may return the cylinder to service."
                  detail="If it removes the failure or the condemnation, it needs the paper behind it — the station's own reading, the photograph — attached to the record you are correcting, and the clearing is marked on the book with your name against it." />
        : null}

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Select name="cx-kind" label="What it should say" value={kind} onChange={setKind}
                options={[{ value: 'periodic_test', label: 'Periodic (hydrostatic) test' },
                          { value: 'visual_inspection', label: 'Visual inspection' },
                          { value: 'valve_replacement', label: 'Valve replacement' },
                          { value: 'repair', label: 'Repair' }]} />
        <Select name="cx-evidence" label="On what evidence" value={evidence} onChange={setEvidence}
                placeholder="Choose — this is never assumed"
                options={[{ value: 'performed', label: 'We did it, or had it done' },
                          { value: 'certificate_sighted', label: 'We have seen the certificate' },
                          { value: 'stamp_observed', label: 'We read the stamping only' }]} />
        <Select name="cx-outcome" label="Outcome" value={outcome} onChange={setOutcome}
                placeholder="Choose"
                options={[{ value: 'passed', label: 'Passed' }, { value: 'failed', label: 'Failed' },
                          { value: 'done', label: 'Done' }, { value: 'not_applicable', label: 'Not applicable' }]} />
        <Text name="cx-on" label="Performed on" value={performedOn} onChange={setPerformedOn} />
        {outcome === 'passed' ? <Text name="cx-until" label="Good until" value={validUntil} onChange={setValidUntil} /> : null}

        {couldClear && mayClear
          ? (ours.length > 0
              ? <Select name="cx-evidence-paper" label="The paper behind it" value={evidenceId} onChange={setEvidenceId}
                        placeholder="Choose the evidence"
                        options={ours.map((p) => ({ value: String(p.attachmentId), label: `${p.filename} — ${p.category.replace(/_/g, ' ')}` }))} />
              : <Banner kind="info" title="There is no paper on this record yet."
                        detail="Attach the station's reading or the photograph to the record you are correcting first — the evidence and the finding must not be separable later." />)
          : null}

        <Text name="cx-why" label="Why the first record was wrong" value={reason} onChange={setReason} />
        <Button kind="primary"
                disabled={runner.busy || !reason.trim() || !evidence || !outcome}
                onClick={() => { void submit(); }}>Append the correction</Button>
      </div>
    </div>
  );
}

function Condemn({ cylinderId, alreadyCondemned, onDone }: {
  cylinderId: number; alreadyCondemned: boolean; onDone: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [performedOn, setPerformedOn] = useState(today());
  const [reason, setReason] = useState('');
  // CONDEMNATION — what the decision rests on. CTS does not decide between condemning
  // and repairing; it records which was decided and on what, so this is required and
  // not defaulted.
  const [basis, setBasis] = useState('');
  const [tareOriginal, setTareOriginal] = useState('');
  const [tarePresent, setTarePresent] = useState('');
  const [permanentSet, setPermanentSet] = useState('');
  // DESTRUCTION — how, and who saw it. "Destroyed" with neither is a claim.
  const [method, setMethod] = useState('');
  const [witness, setWitness] = useState('');

  const act = alreadyCondemned ? 'destruction' : 'condemnation';
  const measured = basis === 'is5844_tare_loss' || basis === 'is5844_permanent_set';

  async function submit() {
    const ok = await runner.run((retryKey) => api.command('cylinder.condemn', {
      cylinderId, act: act as never, performedOn, reason: reason.trim(),
      ...(act === 'condemnation'
        ? { assessment: {
              basis: basis as never,
              ...(tareOriginal.trim() ? { tareOriginalKg: Number(tareOriginal) } : {}),
              ...(tarePresent.trim() ? { tarePresentKg: Number(tarePresent) } : {}),
              ...(permanentSet.trim() ? { permanentSetPct: Number(permanentSet) } : {}),
            } }
        : { destruction: { method: method.trim(), witness: witness.trim() } }),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }

  const ready = act === 'condemnation'
    ? Boolean(basis) && reason.trim().length > 0
    : method.trim().length >= 3 && witness.trim().length >= 3 && reason.trim().length > 0;

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="warn"
              title={alreadyCondemned ? 'Recording that it has been destroyed.' : 'Condemning it is a decision, not the act.'}
              detail={alreadyCondemned
                ? 'This records that the cylinder has been physically rendered unusable. Say how it was done and who saw it — "destroyed" with neither is a claim, and it is the first thing an inspector asks to see.'
                : 'It says the vessel will never return to service. It does NOT destroy it: until that is recorded too, this is still an asset standing somewhere and still has to be accounted for. Say what the decision rests on — the system does not decide between condemning and repairing, it records which was decided.'} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Text name="cd-on" label={alreadyCondemned ? 'Destroyed on' : 'Condemned on'} value={performedOn} onChange={setPerformedOn} />

        {act === 'condemnation' ? <>
          <Select name="cd-basis" label="What it rests on" value={basis} onChange={setBasis}
                  placeholder="Choose"
                  options={[{ value: 'is5844_tare_loss', label: 'Tare loss beyond the limit (IS 5844)' },
                            { value: 'is5844_permanent_set', label: 'Permanent set beyond the limit (IS 5844)' },
                            { value: 'visual_defect', label: 'A visible defect' },
                            { value: 'failed_periodic_test', label: 'It failed its periodic test' },
                            { value: 'customer_instruction', label: "The customer's instruction about his own vessel" },
                            { value: 'other', label: 'Something else — say so below' }]} />
          {basis === 'is5844_tare_loss' ? <>
            <Text name="cd-tare0" label="Original tare (kg)" value={tareOriginal} onChange={setTareOriginal} />
            <Text name="cd-tare1" label="Present tare (kg)" value={tarePresent} onChange={setTarePresent} />
          </> : null}
          {basis === 'is5844_permanent_set'
            ? <Text name="cd-set" label="Permanent set (%)" value={permanentSet} onChange={setPermanentSet} />
            : null}
        </> : <>
          <Text name="cd-method" label="How it was rendered unusable" value={method} onChange={setMethod} />
          <Text name="cd-witness" label="Who saw it" value={witness} onChange={setWitness} />
        </>}

        <Text name="cd-why" label="Why" value={reason} onChange={setReason} />
        <Button kind="danger" disabled={runner.busy || !ready} onClick={() => { void submit(); }}>
          {alreadyCondemned ? 'Record the destruction' : 'Condemn it'}
        </Button>
      </div>
      {measured && !tareOriginal && !permanentSet
        ? <p className="muted">Record the readings that decided it — a limit quoted without its measurement cannot be checked later.</p>
        : null}
    </div>
  );
}

/**
 * THE CERTIFICATE ITSELF (DEF-170).
 *
 * A typed certificate number cannot be shown to an inspector, cannot be checked against
 * the test station's own copy, and is exactly as good as the memory of whoever keyed it.
 * This stores the paper. It is served back hash-checked, under its own permission, and
 * always as a download rather than something a browser will run.
 */
function AttachPaper({ eventId, onDone }: { eventId: number; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [category, setCategory] = useState('test_certificate');
  const [busy, setBusy] = useState(false);

  async function pick(file: File) {
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const b = new Uint8Array(buf);
      let binary = '';
      for (let i = 0; i < b.length; i++) binary += String.fromCharCode(b[i]!);
      const ok = await runner.run((retryKey) => api.command('cylinder.storeMaintenancePaper', {
        eventId, category: category as never,
        filename: file.name, mimeType: file.type || 'application/octet-stream',
        contentBase64: btoa(binary), businessOn: today(),
      }, retryKey ? { idempotencyKey: retryKey } : {}));
      if (ok) onDone();
    } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title="A number you typed is not a certificate."
              detail="Attach the paper itself. It is stored with its hash and served back checked against it, so an altered certificate is refused rather than shown." />
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Select name="mp-cat" label="What this paper is" value={category} onChange={setCategory}
                options={[{ value: 'test_certificate', label: 'Test certificate' },
                          { value: 'test_report', label: 'Test report' },
                          { value: 'condemnation_evidence', label: 'Condemnation evidence' },
                          { value: 'destruction_evidence', label: 'Destruction evidence' }]} />
        <div className="field">
          <label htmlFor={`mp-file-${eventId}`}>The file</label>
          <input id={`mp-file-${eventId}`} type="file" disabled={busy || runner.busy}
                 onChange={(e: { target: { files: FileList | null } }) => {
                   const f = e.target.files?.[0];
                   if (f) void pick(f);
                 }} />
        </div>
        {busy ? <span className="muted">Storing…</span> : null}
      </div>
    </div>
  );
}

/**
 * DUE FOR TEST (0108, DEF-168).
 *
 * Read fresh against today's date — a cylinder appears here because time passed, not
 * because anybody touched it. "Never tested" is on the list on purpose: a cylinder with
 * no test on record is not fine, it is unknown, and unknown is where an accident starts.
 * It sorts last, so it does not crowd out work that has a date on it.
 */
/**
 * DESTROYED, AND STILL COUNTED IN THE FLEET (A06).
 *
 * Every row is a physical fact that has not finished arriving: the vessel was cut up — the
 * method and the witness are on the record — and CTS could not honestly end its registry
 * life, because custody says it is somewhere a life cannot be ended from. Ending it anyway
 * would invent a return, a back date and a position, and what happens to a cylinder
 * destroyed while it is out with a customer is an open ruling (Q11/S6).
 *
 * WHY THIS SCREEN EXISTS AT ALL. Before it, the state lived in one command's reply and
 * nowhere else: after a refresh nobody could find out that a destroyed cylinder was still
 * in the active fleet, and the testing worklist excludes `destroyed`, so it was invisible
 * there too. A reconciliation problem nobody can see is one nobody fixes.
 *
 * Each row says what is in the way and what to do about it, in the same words the command
 * refuses with — a queue and a refusal must not tell an operator two different stories.
 */
function PendingDestruction({ session }: { session: Session }) {
  const asked = useAsk(() => api.view('cylinders.pendingDestruction', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const [why, setWhy] = useState<Record<number, string>>({});
  const rows = asked.data?.rows ?? [];
  if (asked.loading || rows.length === 0) return null;

  async function finish(cylinderId: number) {
    const ok = await runner.run((retryKey) => api.command('cylinder.finalizeDestruction',
      { cylinderId, reason: (why[cylinderId] ?? '').trim() },
      retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setWhy((old) => ({ ...old, [cylinderId]: '' })); asked.reload(); }
  }

  const mayFinish = can(session, 'cylinder.finalize_destruction');
  return (
    <Card className="pending-destruction" title="Destroyed, and still on the books"
          sub="physically destroyed — the registry entry cannot be closed until what is in the way is settled"
          actions={<Pill tone="danger">{count(rows.length)}</Pill>}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="warn" title="These cylinders are counted in the active fleet and do not exist."
              detail="The destruction is recorded, with how it was done and who saw it. CTS will not close a trip, invent a return date or move a vessel to tidy the registry — what happens to a cylinder destroyed while it is out with a customer is an open ruling (Q11/S6). Settle what each row names, then finish it." />
      <Register
        empty="nothing is waiting"
        onOpen={(r) => go(`#/cylinders/${r.cylinderId}`)}
        columns={[
          { key: 'id', head: 'Cylinder', cell: (r) => <Serial>{r.internalId}</Serial> },
          { key: 'serial', head: 'Stamping', cell: (r) => r.serialNo },
          { key: 'when', head: 'Destroyed', cell: (r) => <>{date(r.destroyedOn)}
              <div className="muted">{`${r.daysUnfinalised} day(s) unfinished`}</div></> },
          { key: 'how', head: 'How, and who saw it', cell: (r) => <>{r.method ?? '—'}
              <div className="muted">{r.witness ?? ''}</div></> },
          { key: 'blocker', head: 'What is in the way', cell: (r) => r.blocker },
          { key: 'next', head: 'What to do', cell: (r) => r.nextAction },
          { key: 'finish', head: '', cell: (r) => mayFinish
              ? <span style={{ display: 'flex', gap: 6 }}>
                  <Text name={`fin-why-${r.cylinderId}`} label="" value={why[r.cylinderId] ?? ''}
                        onChange={(v) => setWhy((old) => ({ ...old, [r.cylinderId]: v }))} />
                  <Button small kind="danger" disabled={runner.busy || !(why[r.cylinderId] ?? '').trim()}
                          onClick={() => { void finish(r.cylinderId); }}>Finish it</Button>
                </span>
              : null },
        ] as readonly Column<NonNullable<typeof asked.data>['rows'][number]>[]}
        rows={rows} />
      {!mayFinish
        ? <p className="muted" style={{ marginBottom: 0 }}>Finishing a destruction needs the cylinder.finalize_destruction function. The list is readable without it, because anybody may need to know the fleet count is wrong.</p>
        : null}
    </Card>
  );
}

function DueForTest() {
  const [open, setOpen] = useState(false);
  const due = useAsk(() => api.view('maintenance.due', { pageSize: open ? 25 : 1 }), [open], MESSAGES);
  const total = due.data?.page.total ?? 0;
  if (due.loading || total === 0) return null;
  return (
    <Card title="Due for testing"
          sub="overdue, due within thirty days, or never tested"
          actions={<>
            <Pill tone="danger">{count(total)}</Pill>
            <Button small onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show them'}</Button>
          </>}>
      {!open
        ? <p className="muted">{count(total)} cylinder(s) need a periodic test recorded before they can be filled or sent out.</p>
        : <Register
            empty="nothing is due"
            onOpen={(r) => go(`#/cylinders/${r.cylinderId}`)}
            columns={[
              { key: 'id', head: 'Cylinder', cell: (r) => <Serial>{r.internalId}</Serial> },
              { key: 'serial', head: 'Stamping', cell: (r) => r.serialNo },
              { key: 'gas', head: 'Gas', cell: (r) => words(r.gasService) },
              { key: 'status', head: 'Standing', cell: (r) => <Pill tone={TEST_TONE[r.testStatus] ?? 'muted'}>{TEST_WORDS[r.testStatus] ?? r.testStatus}</Pill> },
              { key: 'due', head: 'Due', cell: (r) => r.nextTestDue
                  ? <>{date(r.nextTestDue)}{r.daysUntilDue !== null && r.daysUntilDue < 0 ? <div className="muted">{`${-r.daysUntilDue} day(s) past`}</div> : null}</>
                  : <span className="muted">never tested</span> },
              { key: 'where', head: 'Where it is', cell: (r) => (r.outWithCustomerName
                  ? <><strong>{r.outWithCustomerName}</strong><div className="muted">
                      {r.outSince ? `out since ${date(r.outSince)}` : 'out with the customer'}</div></>
                  : r.holder ?? '—') },
              // DEF-175 — a cylinder found unfit AFTER it left is the one that matters
              // most, and a worklist that does not say what happens next is a list.
              { key: 'next', head: 'What happens next', cell: (r) => r.followUp },
            ] as readonly Column<NonNullable<typeof due.data>['rows'][number]>[]}
            rows={due.data?.rows ?? []} />}
    </Card>
  );
}
